import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Table,
  Button,
  Spin,
  Descriptions,
  Progress,
  Image,
  Tabs,
  Timeline,
  Empty,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Select,
  message,
  Input,
  Upload,
  Popconfirm,
  Alert,
  Tooltip,
  Switch,
} from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  ScheduleOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
  SmileOutlined,
  FrownOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  UploadOutlined,
  LineChartOutlined,
  EnvironmentOutlined,
  DeleteOutlined,
  EditOutlined,
  FireOutlined,
  UndoOutlined,
  CheckCircleOutlined,
  AlertOutlined,
  SyncOutlined,
  ProfileOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  BankOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import { AppContext } from "../context/AppContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { uploadFile } from "../utils/firebase";
import { LineWave } from "react-loader-spinner";
import calculateCreditScore from "../utils/creditScoreCalculator";
import { calculateGpsFees } from "../utils/gpsFeeCalculations";
import GpsFeeTooltip, { GpsFeeIndicator } from "../components/GpsFeeTooltip";
import { calculateLoanPayments } from "../utils/loanCalculations";
import LoanService from "../utils/loanService";
import { canManageLoans } from "../utils/permissions";
import LoanAgreementGenerator from "../components/LoanAgreementGenerator";
import LoanStatementGenerator from "../components/LoanStatementGenerator";
import JutemStatementGenerator from "../components/JutemStatementGenerator";
import { Space } from "antd";
import GoogleSheetsService from "../utils/googleSheetsService";
import { TransactionHooks } from "../utils/transactionHooks";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const ISSUE_TYPES = [
  { label: "Payment Delay", value: "payment_delay" },
  { label: "Collateral Issue", value: "collateral_issue" },
  { label: "Borrower Communication", value: "communication" },
  { label: "Documentation Problem", value: "documentation" },
  { label: "Fraud Suspected", value: "fraud" },
  { label: "Other", value: "other" },
];

const SEVERITY_LEVELS = [
  { label: "Critical", value: "critical" },
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];

// Helper function to get the effective interest rate for a loan
// Priority: interest_applied > custom_interest_rate > loan_product.interest_rate
// Note: If interest_applied is 0, fall back to custom_interest_rate
const getEffectiveInterestRate = (loanData) => {
  if (!loanData) return 0;

  // First check if interest_applied exists and is not 0 (the source of truth)
  if (
    loanData.interest_applied != null &&
    !isNaN(parseFloat(loanData.interest_applied)) &&
    parseFloat(loanData.interest_applied) !== 0
  ) {
    return parseFloat(loanData.interest_applied);
  }

  // Fall back to custom_interest_rate if it exists and is not 0
  if (
    loanData.custom_interest_rate != null &&
    !isNaN(parseFloat(loanData.custom_interest_rate)) &&
    parseFloat(loanData.custom_interest_rate) !== 0
  ) {
    return parseFloat(loanData.custom_interest_rate);
  }

  // Finally fall back to product default rate
  if (
    loanData.loan_product?.interest_rate != null &&
    !isNaN(parseFloat(loanData.loan_product.interest_rate))
  ) {
    return parseFloat(loanData.loan_product.interest_rate);
  }

  return 0;
};

const LoanDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [collateral, setCollateral] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentForm] = Form.useForm();
  const [uploadingCollateral, setUploadingCollateral] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm] = Form.useForm();
  const [showReversalModal, setShowReversalModal] = useState(false);
  const [selectedTransactionForReversal, setSelectedTransactionForReversal] =
    useState(null);
  const [submittingReversal, setSubmittingReversal] = useState(false);
  const [showFlagIssueModal, setShowFlagIssueModal] = useState(false);
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [existingIssues, setExistingIssues] = useState([]);
  const [showReloanModal, setShowReloanModal] = useState(false);
  const [reloanForm] = Form.useForm();
  const [reloanCalculations, setReloanCalculations] = useState(null);
  const [issueForm] = Form.useForm();
  const { user, company, updateBalance, accounts, updateAccountBalance } =
    useContext(AppContext);
  const [borrowerScore, setBorrowerScore] = useState(0);
  const token = localStorage.getItem("token");
  const [ptps, setPtps] = useState([]);
  const [showPtpModal, setShowPtpModal] = useState(false);
  const [submittingPtp, setSubmittingPtp] = useState(false);
  const [ptpForm] = Form.useForm();
  const [gpsTrackers, setGpsTrackers] = useState([]);
  const [showGpsModal, setShowGpsModal] = useState(false);
  const [submittingGps, setSubmittingGps] = useState(false);
  const [gpsForm] = Form.useForm();
  const [availableTrackers, setAvailableTrackers] = useState([]);
  const [gpsFeeData, setGpsFeeData] = useState(null);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [debts, setDebts] = useState([]);
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [submittingDebt, setSubmittingDebt] = useState(false);
  const [showUploadScheduleModal, setShowUploadScheduleModal] = useState(false);
  const [uploadingSchedule, setUploadingSchedule] = useState(false);
  const [scheduleFile, setScheduleFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("single"); // "single" or "bulk"
  const [scheduleForm] = Form.useForm();
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [editScheduleForm] = Form.useForm();
  const [savingEdit, setSavingEdit] = useState(false);
  const [debtForm] = Form.useForm();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [deletingLoan, setDeletingLoan] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [deleteForm] = Form.useForm();
  const [selectedRefundAccount, setSelectedRefundAccount] = useState(null);
  const [showIncrementModal, setShowIncrementModal] = useState(false);
  const [submittingIncrement, setSubmittingIncrement] = useState(false);
  const [incrementForm] = Form.useForm();
  const [incrementCalculations, setIncrementCalculations] = useState(null);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [submittingAdjustment, setSubmittingAdjustment] = useState(false);
  const [adjustmentForm] = Form.useForm();

  const company_name = company?.company_name || "Kwacha Plus";

  const paymentMethods = [
    { label: "Mobile Money", value: "mobile_money" },
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "Cash", value: "cash" },
    { label: "Credit Card", value: "credit_card" },
    { label: "PMEC", value: "pmec" },
  ];

  const transactionTypes = [
    { label: "Loan Repayment", value: "Loan Repayment" },
    { label: "Discount", value: "Discount" },
    { label: "Debt Repayment", value: "Debt Repayment" },
    { label: "GPS Fee Payment", value: "GPS Fee Payment" },
    { label: "Penalty Fee", value: "Penalty Fee" },
    { label: "Interest Payment", value: "Interest Payment" },
    { label: "Principal Payment", value: "Principal Payment" },
    { label: "Admin Fee", value: "Admin Fee" },
    { label: "Collection Fee", value: "Collection Fee" },
    { label: "Bailiff Fee", value: "Bailiff Fee" },
  ];

  const transactionStatuses = [
    // { label: "Pending", value: "Pending" },
    { label: "Completed", value: "Completed" },
  ];

  const debtTypes = [
    { label: "From Previous Loan", value: "previous_loan" },
    { label: "Legal Fees", value: "legal_fees" },
    { label: "Collection Costs", value: "collection_costs" },
    { label: "Penalty Charges", value: "penalty_charges" },
    { label: "Late Payment Fee", value: "late_payment_fee" },
    { label: "Administrative Costs", value: "admin_costs" },
    { label: "Outstanding Interest", value: "Outstanding Interest" },
    { label: "Other", value: "other" },
  ];

  const statusMap = {
    1: {
      text: "Pending Approval",
      color: "orange",
      icon: <ExclamationCircleOutlined />,
    },
    2: {
      text: "Under Review",
      color: "orange",
      icon: <ExclamationCircleOutlined />,
    },
    3: {
      text: "Incomplete Application",
      color: "orange",
      icon: <ExclamationCircleOutlined />,
    },
    7: { text: "Active", color: "green", icon: <ScheduleOutlined /> },
    8: { text: "Overdue", color: "red", icon: <SafetyCertificateOutlined /> },
    10: { text: "Closed", color: "gray", icon: <FileTextOutlined /> },
    12: {
      text: "App Application",
      color: "purple",
      icon: <ExclamationCircleOutlined />,
    },
  };

  const updateBorrowerCreditScore = async (borrowerId) => {
    try {
      const [borrowerRes, loansRes] = await Promise.all([
        axios.get(`https://zedlenders.pickmesms.com/users/${borrowerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[borrower]=${borrowerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      const newScore = calculateCreditScore(
        borrowerRes.data.data,
        loansRes.data.data,
      );
      setBorrowerScore(newScore);
      console.log("Credit score : " + newScore);

      await axios.patch(
        `https://zedlenders.pickmesms.com/users/${borrowerId}`, // URL
        {
          credit_score: newScore, // Data: the payload for patching
        },
        {
          headers: { Authorization: `Bearer ${token}` }, // Config: headers and other settings
        },
      );
    } catch (error) {
      console.error("Failed to update credit score:", error);
    }
  };

  const getCreditScoreInfo = (score) => {
    if (score >= 800)
      return {
        category: "Exceptional",
        color: "#006400",
        description: "Excellent creditworthiness",
      };
    if (score >= 740)
      return {
        category: "Very Good",
        color: "#228B22",
        description: "Low credit risk",
      };
    if (score >= 670)
      return {
        category: "Good",
        color: "#32CD32",
        description: "Average to good terms",
      };
    if (score >= 580)
      return {
        category: "Fair",
        color: "#FFD700",
        description: "Subprime credit rating",
      };
    return {
      category: "Poor",
      color: "#FF4500",
      description: "High risk borrower",
    };
  };

  // Calculate GPS fees when GPS trackers change
  useEffect(() => {
    if (gpsTrackers && gpsTrackers.length > 0) {
      const calculatedFees = calculateGpsFees(gpsTrackers);
      setGpsFeeData(calculatedFees);
    } else {
      setGpsFeeData(null);
    }
  }, [gpsTrackers]);

  const gpsFees = gpsFeeData ? gpsFeeData.totalFee : 0;

  // Helper function to calculate current interest
  const calculateCurrentInterest = () => {
    if (!loan || !loan.loan_product || !loan.amount) return 0;
    const principal = parseFloat(loan.amount);
    if (isNaN(principal) || principal <= 0) return 0;

    const interestRate = getEffectiveInterestRate(loan);
    if (interestRate <= 0) return 0;

    return Math.round(principal * (interestRate / 100) * 100) / 100;
  };

  // Calculate loan increment details
  const calculateIncrementDetails = (incrementAmount) => {
    if (
      !loan ||
      !loan.loan_product ||
      !incrementAmount ||
      incrementAmount <= 0
    ) {
      return null;
    }

    const currentPrincipal = parseFloat(loan.amount || 0);
    const newPrincipal = currentPrincipal + parseFloat(incrementAmount);
    const loanProduct = loan.loan_product;
    const interestRate = getEffectiveInterestRate(loan);

    // Use the existing calculateLoanPayments function to get accurate interest calculation
    const paymentDetails = calculateLoanPayments(
      loanProduct,
      newPrincipal,
      new Date(loan.start_date || loan.created_at), // Use original loan start date
    );

    const newInterestAmount = paymentDetails.interest_amount;
    const newTotalAmount = paymentDetails.total_amount_due;

    // Calculate current interest using the helper function
    const currentInterest = calculateCurrentInterest();

    const currentTotal = parseFloat(loan.total_amount_due || 0);

    return {
      currentPrincipal,
      incrementAmount: parseFloat(incrementAmount),
      newPrincipal,
      currentInterest,
      newInterestAmount,
      interestIncrease: newInterestAmount - currentInterest,
      currentTotal,
      newTotalAmount,
      totalIncrease: newTotalAmount - currentTotal,
    };
  };

  // Calculate total outstanding debt
  const totalOutstandingDebt = debts.reduce((total, debt) => {
    if (debt.status === "active") {
      const outstandingAmount = Math.max(
        0,
        debt.amount_owing - (debt.amount_paid || 0),
      );
      return total + outstandingAmount;
    }
    return total;
  }, 0);

  const handleRecordPayment = async (values) => {
    try {
      setSubmittingPayment(true);
      const token = localStorage.getItem("token");

      // Check payment type
      const isDebtRepayment = values.transaction_type === "Debt Repayment";
      const isGpsFeePayment = values.transaction_type === "GPS Fee Payment";
      const isInterestPayment = values.transaction_type === "Interest Payment";
      const isPrincipalPayment =
        values.transaction_type === "Principal Payment";
      const isDiscount = values.transaction_type === "Discount";

      // Only validate remaining balance for regular loan payments (not discounts)
      if (
        !isDebtRepayment &&
        !isGpsFeePayment &&
        !isInterestPayment &&
        !isPrincipalPayment &&
        !isDiscount &&
        values.transaction_type === "Loan Repayment" &&
        values.amount > remainingBalance
      ) {
        throw new Error("Payment exceeds remaining balance");
      }

      // For discounts, validate against loan amount (not remaining balance)
      if (isDiscount && values.amount > loan.amount) {
        throw new Error("Discount cannot exceed loan amount");
      }

      // For discount transactions, set default values for hidden required fields
      if (isDiscount) {
        values.payment_method = "cash"; // Default payment method for discounts
        values.reference_number = `DISCOUNT-${Date.now()}`;
        values.account_id = null; // No account needed for discounts
      }

      // Calculate total transaction amount (payment + transfer fees) - not for discounts
      const transferFees = isDiscount ? 0 : values.transfer_fees || 0;
      const totalTransactionAmount = isDiscount
        ? values.amount
        : values.amount + transferFees;

      //new balance = company.balance + total transaction amount (discounts don't affect balance)
      const newBalance = isDiscount
        ? company?.balance
        : company?.balance + totalTransactionAmount;

      // Submit payment
      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/transactions",
        {
          ...values,
          amount: totalTransactionAmount, // Save total amount in transaction
          payment_amount: values.amount, // Save actual payment amount separately
          transfer_fees: transferFees, // Save transfer fees separately
          loan: id,
          transaction_date: values.transaction_date.format("YYYY-MM-DD"),
          user: user.id,
          is_loan_transaction:
            !isDebtRepayment &&
            !isGpsFeePayment &&
            !isInterestPayment &&
            !isPrincipalPayment, // False for debt, GPS, interest, and principal payments
          is_debt_repayment: isDebtRepayment,
          is_gps_fee_payment: isGpsFeePayment,
          new_amount: newBalance,
          company: company?.id,
          account: values.account_id,
          amortization_installment: values.amortization_id || null, // Link to amortization installment for company 22
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Store the new payment but DON'T add it to state yet
      // (we need to calculate interest portion based on PREVIOUS payments only)
      const newPayment = response.data.data;

      // Handle debt repayment differently
      if (isDebtRepayment) {
        // For debt repayments, we need to update the debts collection
        if (values.debt_id) {
          try {
            const selectedDebt = debts.find(
              (debt) => debt.id === values.debt_id,
            );
            if (selectedDebt) {
              const newAmountPaid =
                (selectedDebt.amount_paid || 0) + values.amount;

              console.log("Debt Payment Debug:", {
                debtId: selectedDebt.id,
                amountOwing: selectedDebt.amount_owing,
                previousAmountPaid: selectedDebt.amount_paid || 0,
                newPayment: values.amount,
                newAmountPaid: newAmountPaid,
                willBeSettled: newAmountPaid >= selectedDebt.amount_owing,
              });

              const updatedDebt = {
                ...selectedDebt,
                amount_paid: Math.min(newAmountPaid, selectedDebt.amount_owing), // Cap at amount owing
                status:
                  newAmountPaid >= selectedDebt.amount_owing
                    ? "settled"
                    : "active",
              };

              // Update debt in backend
              await axios.patch(
                `https://zedlenders.pickmesms.com/items/debts/${values.debt_id}`,
                {
                  amount_paid: updatedDebt.amount_paid, // Use the capped amount
                  status: updatedDebt.status,
                },
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              );

              // Update local debts state
              setDebts(
                debts.map((debt) =>
                  debt.id === values.debt_id ? updatedDebt : debt,
                ),
              );
            }
          } catch (debtUpdateError) {
            console.error("Failed to update debt:", debtUpdateError);
            message.warning(
              "Payment recorded but failed to update debt status",
            );
          }
        }

        message.success({
          content: "Debt payment recorded successfully!",
          icon: <SmileOutlined />,
        });
      } else if (isGpsFeePayment) {
        // GPS fee payment - track separately, don't add to loan paid_amount
        message.success({
          content: "GPS fee payment recorded successfully!",
          icon: <SmileOutlined />,
        });
      } else if (isInterestPayment) {
        // Interest payment - track separately, don't add to loan paid_amount
        // This only reduces the interest owed, not the principal
        message.success({
          content: "Interest payment recorded successfully!",
          icon: <SmileOutlined />,
        });
      } else if (isPrincipalPayment) {
        // Principal payment - add to loan paid_amount (reduces principal only)
        const newPaidAmount = (loan.paid_amount || 0) + values.amount;
        setLoan({
          ...loan,
          paid_amount: newPaidAmount,
          loan_status: newPaidAmount >= loan.amount ? 10 : 7,
          last_payment_date: values.transaction_date.format("YYYY-MM-DD"),
        });

        message.success({
          content: "Principal payment recorded successfully!",
          icon: <SmileOutlined />,
        });
      } else {
        // Regular loan payment (Loan Repayment) - add to loan paid_amount
        const newPaidAmount = (loan.paid_amount || 0) + values.amount;
        setLoan({
          ...loan,
          paid_amount: newPaidAmount,
          loan_status: newPaidAmount >= remainingBalance ? 10 : 7,
          last_payment_date: values.transaction_date.format("YYYY-MM-DD"),
        });

        // Update amortization installment status for company 22
        if (company?.id === 22 && values.amortization_id) {
          try {
            await axios.patch(
              `https://zedlenders.pickmesms.com/items/amortization/${values.amortization_id}`,
              {
                status: "paid",
              },
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );

            // Update local amortization schedule state
            setAmortizationSchedule((prevSchedule) =>
              prevSchedule.map((inst) =>
                inst.id === values.amortization_id
                  ? { ...inst, status: "paid" }
                  : inst,
              ),
            );

            console.log(
              "✅ Amortization installment marked as paid:",
              values.amortization_id,
            );
          } catch (amortError) {
            console.error(
              "Failed to update amortization installment:",
              amortError,
            );
            message.warning(
              "Payment recorded but failed to update installment status",
            );
          }
        }

        message.success({
          content: "Payment recorded successfully!",
          icon: <SmileOutlined />,
        });
      }

      paymentForm.resetFields();
      setShowPaymentModal(false);

      if (values.transaction_status === "Completed") {
        //Update new Balance
        updateBalance(newPayment.new_amount);

        // Update the selected account balance with total transaction amount
        const selectedAccount = accounts.find(
          (acc) => acc.id === values.account_id,
        );
        if (selectedAccount) {
          const newAccountBalance =
            selectedAccount.balance + totalTransactionAmount;
          updateAccountBalance(values.account_id, newAccountBalance);

          // Update account balance in backend
          await axios.patch(
            `https://zedlenders.pickmesms.com/items/accounts/${values.account_id}`,
            {
              balance: newAccountBalance,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
        }

        await axios.patch(
          `https://zedlenders.pickmesms.com/items/loans/${id}`,
          {
            paid_amount: loan.paid_amount + values.amount, // Only the actual payment amount, not transfer fees
            loan_status: values.amount >= remainingBalance ? 10 : 7,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        updateBorrowerCreditScore(loan.borrower.id);

        // Record double-entry accounting for repayment
        console.log("🔵 Starting double-entry accounting for payment", {
          transactionType: values.transaction_type,
          amount: values.amount,
          accountId: values.account_id,
        });

        try {
          // Calculate principal vs interest portions
          const totalInterestDue = loan.interest_amount || 0;
          const totalPrincipalDue = loan.amount || 0;
          const totalPrincipalPaid = loan.paid_amount || 0;

          // Calculate how much interest has been paid by checking JOURNAL ENTRIES (source of truth)
          // Query the Interest Income account to see actual interest revenue recorded
          let totalInterestAlreadyPaid = 0;

          try {
            const journalResponse = await axios.get(
              `https://zedlenders.pickmesms.com/items/journal_entries`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                  filter: {
                    company: company?.id,
                    reference: { _starts_with: `REPAY-${id}` }, // Get repayments for THIS loan only
                  },
                  fields: "journal_entry_lines.*",
                },
              },
            );

            const entries = journalResponse.data.data || [];

            // Sum up all credits to Interest Income account (4000)
            entries.forEach((entry) => {
              if (entry.journal_entry_lines) {
                entry.journal_entry_lines.forEach((line) => {
                  if (line.account_code === "4000") {
                    // Interest Income
                    totalInterestAlreadyPaid += parseFloat(
                      line.credit_amount || 0,
                    );
                  }
                });
              }
            });

            console.log(
              "📊 Interest already recorded in journal:",
              totalInterestAlreadyPaid,
            );
          } catch (journalError) {
            console.error("Error fetching journal entries:", journalError);
            // Fallback to simple calculation if journal query fails
            const interestPayments = payments.filter(
              (p) => p.transaction_type === "Interest Payment",
            );
            totalInterestAlreadyPaid = interestPayments.reduce(
              (sum, p) => sum + parseFloat(p.amount || 0),
              0,
            );
          }

          const remainingInterest = Math.max(
            0,
            totalInterestDue - totalInterestAlreadyPaid,
          );

          // If it's a principal payment, it's all principal
          // If it's interest payment, it's all interest
          // If it's regular loan repayment, split it (interest first, then principal)
          let principalAmount = 0;
          let interestAmount = 0;

          if (isPrincipalPayment) {
            principalAmount = values.amount;
          } else if (isInterestPayment) {
            interestAmount = values.amount;
          } else if (!isDebtRepayment && !isGpsFeePayment && !isDiscount) {
            // Regular loan repayment - apply to interest first, then principal
            // This is standard accounting practice: payments reduce interest first
            interestAmount = Math.min(values.amount, remainingInterest);
            principalAmount = values.amount - interestAmount;

            console.log("Loan Repayment Interest Calculation:", {
              paymentAmount: values.amount,
              totalInterestDue,
              totalInterestAlreadyPaid,
              remainingInterest,
              calculatedInterestPortion: interestAmount,
              calculatedPrincipalPortion: principalAmount,
            });
          }

          // Only record if this is a loan repayment (not debt, GPS, or discount)
          if (
            (principalAmount > 0 || interestAmount > 0) &&
            values.account_id
          ) {
            console.log("🟢 Calling TransactionHooks.onLoanRepayment with:", {
              principalAmount,
              interestAmount,
              total: principalAmount + interestAmount,
            });

            await TransactionHooks.onLoanRepayment({
              loanId: id,
              principalAmount,
              interestAmount,
              date: values.transaction_date.toDate(),
              company: company?.id,
              token,
              accountId: values.account_id,
            });

            console.log(
              "✅ TransactionHooks.onLoanRepayment completed successfully",
            );
          } else {
            console.log("⚠️ Skipping double-entry: ", {
              principalAmount,
              interestAmount,
              hasAccountId: !!values.account_id,
              reason: !values.account_id
                ? "No account_id"
                : "Both amounts are zero",
            });
          }

          // Record fee income for GPS fees and admin fees
          if (isGpsFeePayment && values.account_id) {
            await TransactionHooks.onFeeIncome({
              feeType: "GPS Fee Payment",
              amount: values.amount,
              date: values.transaction_date.toDate(),
              company: company?.id,
              token,
              loanId: id,
              accountId: values.account_id,
            });
          }
        } catch (doubleEntryError) {
          console.error(
            "Error recording double-entry for payment:",
            doubleEntryError,
          );
          // Continue even if double-entry fails
        }

        // NOW update local state with the new payment (after double-entry accounting is done)
        setPayments([newPayment, ...payments]);

        // Update Google Sheets with payment
        try {
          const googleSheetsService = new GoogleSheetsService();
          const newPaidAmount = (loan.paid_amount || 0) + values.amount;
          const newStatus = values.amount >= remainingBalance ? 10 : 7;

          await googleSheetsService.updateLoanInSheet(id, {
            ...loan,
            paid_amount: newPaidAmount,
            loan_status: newStatus,
            last_payment_date: values.transaction_date.format("YYYY-MM-DD"),
          });
          console.log("✅ Payment updated in Google Sheets");
        } catch (googleSheetsError) {
          console.error(
            "❌ Failed to update Google Sheets:",
            googleSheetsError,
          );
          // Don't fail the payment if Google Sheets update fails
        }
      }
    } catch (error) {
      message.error({
        content: error.response?.data?.errors?.[0]?.message || error.message,
        icon: <FrownOutlined />,
      });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleReloan = async () => {
    const token = localStorage.getItem("token");

    console.log("Reloan button clicked, loan:", loan);

    // Calculate remaining principal and outstanding interest
    try {
      // Get all payment transactions to properly categorize them
      const transactionsResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/transactions?filter[loan]=${loan.id}&filter[transaction_status]=Completed&fields=amount,transaction_type`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const transactions = transactionsResponse.data.data || [];

      // Separate different types of payments
      const interestPayments = transactions.filter(
        (t) => t.transaction_type === "Interest Payment",
      );
      const principalPayments = transactions.filter(
        (t) => t.transaction_type === "Principal Payment",
      );
      const loanRepayments = transactions.filter(
        (t) => t.transaction_type === "Loan Repayment",
      );

      const totalInterestPaid = interestPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.amount || 0),
        0,
      );

      const totalPrincipalPayments = principalPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.amount || 0),
        0,
      );

      const totalLoanRepayments = loanRepayments.reduce(
        (sum, payment) => sum + parseFloat(payment.amount || 0),
        0,
      );

      const originalAmount = parseFloat(loan.amount || 0);

      // Calculate expected interest using the same logic as the UI
      const calculateExpectedInterest = () => {
        if (!loan || !loan.loan_product || !loan.amount) return 0;
        const principal = parseFloat(loan.amount);
        if (isNaN(principal) || principal <= 0) return 0;

        const interestRate = getEffectiveInterestRate(loan);
        if (interestRate <= 0) return 0;

        return Math.round(principal * (interestRate / 100) * 100) / 100;
      };

      const expectedInterest = calculateExpectedInterest();

      // Calculate how much of the loan repayments went to principal
      // If total loan repayments exceed expected interest, the excess goes to principal
      const loanRepaymentsToPrincipal = Math.max(
        0,
        totalLoanRepayments - Math.max(0, expectedInterest - totalInterestPaid),
      );

      const principalPaid = Math.min(
        originalAmount,
        totalPrincipalPayments + loanRepaymentsToPrincipal,
      );
      const remainingPrincipal = originalAmount - principalPaid;

      const interestAmount = expectedInterest;
      const outstandingInterest = Math.max(
        0,
        interestAmount - totalInterestPaid,
      );

      const totalAmountDue = parseFloat(loan.total_amount_due || 0);

      const calculations = {
        originalAmount,
        totalAmountDue,
        interestAmount,
        totalPaid:
          totalPrincipalPayments + totalLoanRepayments + totalInterestPaid, // Total of all payments made
        principalPaid,
        remainingPrincipal,
        interestPaid: totalInterestPaid,
        outstandingInterest,
        reloanAmount: remainingPrincipal, // Only reloan the remaining principal
      };

      setReloanCalculations(calculations);
      setShowReloanModal(true);

      // Set initial form values
      reloanForm.setFieldsValue({
        reloan_amount: remainingPrincipal.toFixed(2),
        outstanding_debt: outstandingInterest.toFixed(2),
      });
    } catch (error) {
      console.error("Error calculating reloan amounts:", error);
      console.error("Error details:", error.response?.data || error.message);
      message.error(
        `Failed to calculate reloan amounts: ${
          error.response?.data?.message || error.message
        }`,
      );
    }
  };

  const handleReloanSubmit = async (values) => {
    const token = localStorage.getItem("token");

    try {
      // Create new loan with the remaining principal amount
      const reloanAmount = parseFloat(values.reloan_amount);
      const outstandingDebt = parseFloat(values.outstanding_debt);

      // Use selected reloan date or default to today
      const reloanDate = values.reloan_date
        ? values.reloan_date.format("YYYY-MM-DD")
        : new Date().toISOString().split("T")[0];
      const reloanDateObj = values.reloan_date
        ? values.reloan_date.toDate()
        : new Date();

      // Calculate new payment schedule
      const paymentDetails = calculateLoanPayments(
        loan.loan_product,
        reloanAmount,
        reloanDateObj,
      );

      // Create the new loan
      const newLoanPayload = {
        borrower: loan.borrower?.id || loan.borrower,
        loan_product: loan.loan_product?.id || loan.loan_product,
        company: company.id,
        amount: reloanAmount,
        application_date: reloanDate,
        loan_status: 7, // Set as active immediately
        status: "published",
        next_payment_date: paymentDetails.next_payment_date,
        next_payment_amount: paymentDetails.next_payment_amount,
        total_amount_due: paymentDetails.total_amount_due,
        interest_amount: paymentDetails.interest_amount,
        ...(loan.loan_product?.auto_split_payments &&
          paymentDetails.payment_schedule && {
            payment_schedule: paymentDetails.payment_schedule,
          }),
        is_reloan: true,
        original_loan_id: loan.id,
        // Copy branch if exists
        ...(loan.branch && {
          branch: loan.branch.id || loan.branch,
        }),
        // Copy collateral if exists
        ...(loan.collateral && {
          collateral: loan.collateral.id || loan.collateral,
        }),
      };

      const newLoanResponse = await axios.post(
        "https://zedlenders.pickmesms.com/items/loans",
        newLoanPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Record outstanding interest as debt if there's any
      if (outstandingDebt > 0) {
        const debtPayload = {
          debtor_id: loan.borrower?.id || loan.borrower,
          loan_id: loan.id,
          amount_owing: outstandingDebt,
          amount_paid: 0,
          debt_type: "Outstanding Interest",
          description: `Outstanding interest from reloaned loan #${loan.id}. New loan created: #${newLoanResponse.data.data.id}`,
          status: "active",
          company: company.id,
          date_created: new Date().toISOString(),
        };

        await axios.post(
          "https://zedlenders.pickmesms.com/items/debts",
          debtPayload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      // Update original loan to mark it as reloaned
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${loan.id}`,
        {
          loan_status: 10, // Set to a "Reloaned" status
          reloaned: (loan.reloaned || 0) + 1,
          reloan_date: reloanDate,
          new_loan_id: newLoanResponse.data.data.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      message.success("Loan reloaned successfully!");
      setShowReloanModal(false);
      reloanForm.resetFields();
      setReloanCalculations(null);

      // Navigate to the new loan
      navigate(`/loan/${newLoanResponse.data.data.id}`);
    } catch (error) {
      console.error("Error processing reloan:", error);
      console.error("Error details:", error.response?.data || error.message);
      message.error(
        `Failed to process reloan: ${
          error.response?.data?.message || error.message
        }`,
      );
    }
  };

  const handleCollateralUpload = async (values) => {
    try {
      setUploadingCollateral(true);
      const token = localStorage.getItem("token");

      // First, upload files to Firebase
      const fileList = values.collateralImages || [];

      if (fileList.length === 0) {
        message.error("Please select at least one image to upload");
        return;
      }

      const uploadPromises = fileList.map(async (file) => {
        return await uploadFile(file.originFileObj, id);
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      // Create collateral record
      const collateralData = {
        loan: id,
        description: values.description || "Uploaded collateral documents",
        value: values.value || 0,
        documents: JSON.stringify(uploadedUrls),
      };

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/collateral_register",
        collateralData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Update local collateral state
      const apiData = response.data.data;
      let parsedDocuments = uploadedUrls;

      // Parse documents if they come as string from API
      if (typeof apiData.documents === "string") {
        try {
          parsedDocuments = JSON.parse(apiData.documents);
        } catch (e) {
          parsedDocuments = uploadedUrls; // fallback
        }
      }

      const newCollateral = {
        ...apiData,
        documents: parsedDocuments,
        status: apiData.status || "active", // Ensure status exists
      };

      setCollateral((prev) => [...prev, newCollateral]);

      message.success("Collateral documents uploaded successfully");
      setShowUploadModal(false);
      uploadForm.resetFields();
    } catch (error) {
      console.error("Collateral upload error:", error);
      message.error(
        error.response?.data?.errors?.[0]?.message ||
          "Failed to upload collateral documents",
      );
    } finally {
      setUploadingCollateral(false);
    }
  };

  const handleTransactionReversal = async (transaction) => {
    try {
      setSubmittingReversal(true);
      const token = localStorage.getItem("token");

      console.log("Reversing transaction:", transaction);

      // Determine transaction impact for reversal
      const isPayment = transaction.transaction_type === "Loan Repayment";
      const isDebtPayment = transaction.transaction_type === "Debt Repayment";
      const isGpsPayment = transaction.transaction_type === "GPS Fee Payment";
      const isDisbursement =
        transaction.transaction_type === "Loan Disbursement";
      const isAdminFees = transaction.transaction_type === "Admin Fees";
      const isTransferFees = transaction.transaction_type === "Transfer Fees";

      // Create offsetting (reversal) transaction with negative amount
      const reversalTransaction = {
        amount: -transaction.amount, // Negative amount for reversal
        payment_amount: transaction.payment_amount
          ? -transaction.payment_amount
          : null,
        transfer_fees: transaction.transfer_fees
          ? -transaction.transfer_fees
          : null,
        loan: transaction.loan,
        transaction_date: new Date().toISOString().split("T")[0],
        user: user.id,
        transaction_type: `${transaction.transaction_type} Reversal`,
        transaction_status: "Completed",
        reference_number: `REV-${
          transaction.reference_number || Math.floor(Math.random() * 1000000)
        }`,
        company: company?.id,
        account:
          transaction.account ||
          transaction.from_account ||
          transaction.to_account,
        from_account: transaction.to_account, // Swap for reversal
        to_account: transaction.from_account, // Swap for reversal
        notes: `Reversal of transaction ${transaction.reference_number} - ${
          transaction.notes || ""
        }`,
        is_loan_transaction: transaction.is_loan_transaction,
        is_debt_repayment: transaction.is_debt_repayment,
        is_gps_fee_payment: transaction.is_gps_fee_payment,
        reversed_transaction_id: transaction.id,
        is_reversal: true,
      };

      // Create the reversal transaction
      const reversalResponse = await axios.post(
        `https://zedlenders.pickmesms.com/items/transactions`,
        reversalTransaction,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Update the original transaction to mark it as reversed
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/transactions/${transaction.id}`,
        {
          is_reversed: true,
          reversed_by: user.id,
          reversed_date: new Date().toISOString(),
          reversal_transaction_id: reversalResponse.data.data.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Now update the affected balances based on transaction type
      if (isPayment) {
        // Reverse loan payment - reduce paid_amount
        const newPaidAmount = Math.max(
          0,
          (loan.paid_amount || 0) - transaction.amount,
        );
        const newStatus = newPaidAmount === 0 ? 7 : loan.loan_status; // Set back to active if fully reversed

        await axios.patch(
          `https://zedlenders.pickmesms.com/items/loans/${id}`,
          {
            paid_amount: newPaidAmount,
            loan_status: newStatus,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setLoan({
          ...loan,
          paid_amount: newPaidAmount,
          loan_status: newStatus,
        });
      } else if (isDebtPayment && transaction.debt_id) {
        // Reverse debt payment - reduce debt's amount_paid
        const targetDebt = debts.find((d) => d.id === transaction.debt_id);
        if (targetDebt) {
          const newAmountPaid = Math.max(
            0,
            (targetDebt.amount_paid || 0) - transaction.amount,
          );
          const newStatus =
            newAmountPaid < targetDebt.amount_owing ? "active" : "settled";

          await axios.patch(
            `https://zedlenders.pickmesms.com/items/debts/${transaction.debt_id}`,
            {
              amount_paid: newAmountPaid,
              status: newStatus,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          // Update local debts state
          setDebts(
            debts.map((debt) =>
              debt.id === transaction.debt_id
                ? { ...debt, amount_paid: newAmountPaid, status: newStatus }
                : debt,
            ),
          );
        }
      } else if (isDisbursement) {
        // Reverse disbursement - set loan back to pending approval and reset disbursement
        // (Account balance will be handled in the comprehensive section below)
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/loans/${id}`,
          {
            loan_status: 1, // Pending approval
            approval_date: null,
            disbursed_amount: 0, // Reset disbursed amount
            disbursement_date: null, // Clear disbursement date
            paid_amount: 0, // Reset any payments
            next_payment_date: null, // Clear payment schedule
            last_payment_date: null, // Clear last payment
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setLoan({
          ...loan,
          loan_status: 1,
          approval_date: null,
          disbursed_amount: 0,
          disbursement_date: null,
          paid_amount: 0,
          next_payment_date: null,
          last_payment_date: null,
        });
      }

      // Update account balances for all transaction types
      console.log("Transaction details for balance update:", {
        id: transaction.id,
        type: transaction.transaction_type,
        amount: transaction.amount,
        from_account: transaction.from_account,
        to_account: transaction.to_account,
        account: transaction.account,
        payment_amount: transaction.payment_amount,
        transfer_fees: transaction.transfer_fees,
      });

      // Determine which accounts to update based on transaction type and fields
      const accountsToUpdate = [];

      if (isDisbursement) {
        // Disbursement: Money went OUT of from_account, reversal puts it back IN
        if (transaction.from_account) {
          accountsToUpdate.push({
            accountId: transaction.from_account,
            adjustment: +transaction.amount, // Add money back
            reason: "Disbursement reversal - returning money to account",
          });
        }
      } else if (isPayment || isDebtPayment || isGpsPayment) {
        // Payments: Money came IN to to_account or account, reversal takes it OUT
        const targetAccount = transaction.to_account || transaction.account;
        if (targetAccount) {
          accountsToUpdate.push({
            accountId: targetAccount,
            adjustment: -transaction.amount, // Remove money
            reason: `${transaction.transaction_type} reversal - removing payment from account`,
          });
        }
      } else if (isAdminFees) {
        // Admin fees: Usually money IN to to_account, reversal takes it OUT
        if (transaction.to_account) {
          accountsToUpdate.push({
            accountId: transaction.to_account,
            adjustment: -transaction.amount, // Remove money
            reason: "Admin fees reversal - removing fee revenue from account",
          });
        }
      } else if (isTransferFees) {
        // Transfer fees: Usually deducted FROM from_account, reversal adds it back
        if (transaction.from_account) {
          accountsToUpdate.push({
            accountId: transaction.from_account,
            adjustment: +transaction.amount, // Add money back
            reason: "Transfer fees reversal - refunding fees to account",
          });
        }
      }

      // Update all identified accounts
      for (const accountUpdate of accountsToUpdate) {
        try {
          console.log(`Processing account update:`, accountUpdate);

          // Get current account balance
          const accountResponse = await axios.get(
            `https://zedlenders.pickmesms.com/items/accounts/${accountUpdate.accountId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const currentBalance = parseFloat(accountResponse.data.data.balance);
          const newBalance = currentBalance + accountUpdate.adjustment;

          console.log(`Account ${accountUpdate.accountId} balance update:`, {
            currentBalance: currentBalance,
            adjustment: accountUpdate.adjustment,
            newBalance: newBalance,
            reason: accountUpdate.reason,
          });

          // Update account balance in database
          await axios.patch(
            `https://zedlenders.pickmesms.com/items/accounts/${accountUpdate.accountId}`,
            { balance: newBalance },
            { headers: { Authorization: `Bearer ${token}` } },
          );

          // Update account balance in AppContext state
          if (updateAccountBalance) {
            updateAccountBalance(accountUpdate.accountId, newBalance);
          }

          console.log(
            `✅ Account ${accountUpdate.accountId} balance updated: ${currentBalance} → ${newBalance}`,
          );
        } catch (accountError) {
          console.error(
            `Failed to update account ${accountUpdate.accountId}:`,
            accountError,
          );
          message.warning(
            `Transaction reversed but failed to update account ${accountUpdate.accountId}. Please check account balances manually.`,
          );
        }
      }

      if (accountsToUpdate.length === 0) {
        console.warn(
          "No accounts identified for balance update. Transaction details:",
          {
            type: transaction.transaction_type,
            from_account: transaction.from_account,
            to_account: transaction.to_account,
            account: transaction.account,
          },
        );
        message.warning(
          "Transaction reversed but no account balance updates were identified. Please check account balances manually.",
        );
      }

      // Refresh loan data and payments/transactions data
      const [loanResponse, paymentsResponse] = await Promise.all([
        axios.get(
          `https://zedlenders.pickmesms.com/items/loans/${id}?fields=*.*,borrower.id,borrower.first_name,borrower.last_name,borrower.nrc,borrower.phone,borrower.email,borrower.address,borrower.credit_score,loan_product.*.*,collateral.*.*`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        axios.get(
          `https://zedlenders.pickmesms.com/items/transactions?filter[loan]=${id}&sort=-date_created`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      setLoan(loanResponse.data.data);
      setPayments(paymentsResponse.data.data);

      message.success({
        content: `Transaction reversed successfully! Reversal reference: ${reversalTransaction.reference_number}`,
        duration: 6,
      });

      setShowReversalModal(false);
      setSelectedTransactionForReversal(null);
    } catch (error) {
      console.error("Transaction reversal error:", error);
      message.error(
        "Failed to reverse transaction: " +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setSubmittingReversal(false);
    }
  };

  const handleFlagIssue = async (values) => {
    try {
      setSubmittingIssue(true);
      const token = localStorage.getItem("token");

      // Upload files to Firebase and get URLs
      const attachmentUrls = [];
      if (values.attachments && values.attachments.fileList) {
        const uploadPromises = values.attachments.fileList.map(async (file) => {
          const url = await uploadFile(file.originFileObj, id); // Use your Firebase upload function
          return url;
        });
        attachmentUrls.push(...(await Promise.all(uploadPromises)));
      }

      // Prepare payload for Directus
      const payload = {
        ...values,
        loan_id: id,
        status: "open",
        reported_by: user.id, // Replace with actual user ID from your auth system
        reported_date: new Date().toISOString(),
        attachments: attachmentUrls, // Save Firebase URLs
        followup_date: values.followup_date.format("YYYY-MM-DD"), // Format date
      };

      // Submit to Directus
      await axios.post(
        "https://zedlenders.pickmesms.com/items/loan_issues",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      message.success({
        content: "Issue reported successfully!",
        icon: <SmileOutlined />,
      });
      issueForm.resetFields();
      setShowFlagIssueModal(false);

      // Refresh issues list
      const res = await axios.get(
        `https://zedlenders.pickmesms.com/items/loan_issues?filter[loan_id]=${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setExistingIssues(res.data.data);
    } catch (error) {
      message.error({
        content: error.response?.data?.errors?.[0]?.message || error.message,
        icon: <FrownOutlined />,
      });
    } finally {
      setSubmittingIssue(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          loanRes,
          collateralRes,
          paymentsRes,
          loanIssues,
          gpsTrackersRes,
          availableTrackersRes,
          debtsRes,
        ] = await Promise.all([
          axios.get(
            `https://zedlenders.pickmesms.com/items/loans/${id}?fields=*,borrower.id,borrower.first_name,borrower.last_name,borrower.nrc,borrower.phone,borrower.email,borrower.address,borrower.credit_score,ptps.*,loan_product.*,gps_trackers.*`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/collateral_register?filter[loan]=${id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/transactions?filter[loan]=${id}&filter[company]=${company?.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/loan_issues?filter[loan_id]=${id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/gps_trackers?filter[assigned_loan]=${id}&filter[company]=${company?.id}&fields=*`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/gps_trackers?filter[status]=not_assigned&filter[company]=${company?.id}&fields=*`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/debts?filter[loan_id]=${id}&filter[company]=${company?.id}&fields=*`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);

        // Parse documents here
        const parsedCollateral = collateralRes.data.data.map((item) => {
          let docs = item.documents;

          // If it's a string, parse it to an array
          if (typeof docs === "string") {
            try {
              docs = JSON.parse(docs);
            } catch (e) {
              docs = []; // fallback if JSON parsing fails
            }
          }

          return {
            ...item,
            documents: docs,
          };
        });

        setLoan(loanRes.data.data);
        setCollateral(parsedCollateral);
        setPayments(paymentsRes.data.data);
        setExistingIssues(loanIssues.data.data);
        setBorrowerScore(loanRes.data.data.borrower.credit_score);
        setPtps(loanRes.data.data.ptps);
        setGpsTrackers(gpsTrackersRes.data.data);
        setAvailableTrackers(availableTrackersRes.data.data);
        setDebts(debtsRes.data.data);

        // Check if user can delete loans (admin only)
        const userRole = localStorage.getItem("userRole");
        const canManage = await canManageLoans();
        const isAdmin =
          userRole === "Administrator" ||
          userRole === "admin" ||
          userRole === "186a1cb5-7b41-4b0e-b9eb-187cb5de75b1";

        // Administrators can always delete loans
        const finalCanDelete = isAdmin || (canManage && isAdmin);

        // Debug logging (you can remove this later)
        console.log("🔍 Delete Permission Check:", {
          userRole,
          canManage,
          isAdmin,
          finalCanDelete,
        });

        setCanDelete(finalCanDelete);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, borrowerScore]);

  // Fetch amortization schedule for company 22
  useEffect(() => {
    if (company?.id !== 22 || !id || !token) return;
    const fetchAmortization = async () => {
      try {
        const res = await axios.get(
          `https://zedlenders.pickmesms.com/items/amortization`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              "filter[loan_reference][_eq]": id,
              fields: "*",
              sort: "due_date",
              limit: -1,
            },
          },
        );
        setAmortizationSchedule(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch amortization:", err);
      }
    };
    fetchAmortization();
  }, [id, company, token]);

  // For Jutem (company 22), derive metric-card values from the amortization
  // schedule rather than the loan record so paid installments don't inflate
  // the displayed principal/interest. "Outstanding" = pending only.
  const jutemMetrics = useMemo(() => {
    if (company?.id !== 22) return null;
    const isPaid = (s) =>
      ["paid", "cleared"].includes((s || "").toLowerCase());
    const pending = amortizationSchedule.filter((i) => !isPaid(i.status));
    const paid = amortizationSchedule.filter((i) => isPaid(i.status));

    const sum = (rows, key) =>
      rows.reduce((acc, r) => acc + parseFloat(r[key] || 0), 0);

    const pendingPrincipal = sum(pending, "amount_due");
    const pendingExpected = sum(pending, "expected_amount");
    const paidExpected = sum(paid, "expected_amount");

    return {
      pending,
      totalLoaned: pendingPrincipal,
      interest: pendingExpected - pendingPrincipal,
      paid: paidExpected,
    };
  }, [company, amortizationSchedule]);

  // Calculate expected interest for this loan
  const expectedInterest = (() => {
    if (!loan || !loan.loan_product || !loan.amount) return 0;

    const loanProduct = loan.loan_product;
    const principal = parseFloat(loan.amount);

    // Validate essential fields
    if (isNaN(principal) || principal <= 0) return 0;

    // Use the helper function to get the correct interest rate (applied > custom > product default)
    const interestRate = getEffectiveInterestRate(loan);
    if (interestRate <= 0) return 0;

    let interestAmount = 0;

    try {
      if (loanProduct.interest_type === "simple") {
        // Get loan term for FV calculation
        const loanTerm = parseFloat(
          loan.loan_term ?? loanProduct.loan_term ?? 1,
        );

        if (loanProduct.simple_interest_method === "flat_rate") {
          // Simple flat rate: principal * rate
          interestAmount = principal * (interestRate / 100);
        } else {
          // FV Formula (default for simple interest): principal * (1 + rate * time) - principal
          // This is the Excel FV formula: FV(rate, nper, 0, -pv)
          const expectedReturn =
            principal * (1 + (interestRate / 100) * loanTerm);
          interestAmount = expectedReturn - principal;
        }
      } else if (loanProduct.interest_type === "compound") {
        // Validate required fields for compound interest
        const resolvedLoanTerm = loan.loan_term ?? loanProduct.loan_term;
        if (
          !resolvedLoanTerm ||
          !loanProduct.term_unit ||
          !loanProduct.payment_frequency ||
          !loanProduct.compound_frequency
        ) {
          // Fallback to simple interest if compound data is incomplete
          interestAmount = principal * (interestRate / 100);
        } else {
          // For compound interest, calculate using compound formula over the loan term
          const daysPerUnit = {
            days: 1,
            weeks: 7,
            months: 30.44,
            years: 365.25,
          };
          const periodsPerYear = {
            daily: 365,
            weekly: 52,
            bi_weekly: 26,
            "3_weeks": 17.39,
            monthly: 12,
            yearly: 1,
          };
          const paymentIntervalDays = {
            daily: 1,
            weekly: 7,
            bi_weekly: 14,
            "3_weeks": 21,
            monthly: 30.44,
            yearly: 365.25,
          };

          const loanTerm = parseFloat(resolvedLoanTerm);
          if (isNaN(loanTerm) || loanTerm <= 0) {
            interestAmount = principal * (interestRate / 100);
          } else {
            const term_in_days =
              loanTerm * (daysPerUnit[loanProduct.term_unit] || 30.44);
            const payment_interval_in_days =
              paymentIntervalDays[loanProduct.payment_frequency] || 30.44;
            const number_of_payments = Math.ceil(
              term_in_days / payment_interval_in_days,
            );

            const m = periodsPerYear[loanProduct.compound_frequency] || 12;
            const p = periodsPerYear[loanProduct.payment_frequency] || 12;
            const r_annual = interestRate / 100;

            if (m > 0 && p > 0 && number_of_payments > 0) {
              const effective_rate_per_payment =
                Math.pow(1 + r_annual / m, m / p) - 1;
              const r = effective_rate_per_payment;
              const n = number_of_payments;

              if (r > 0 && n > 0) {
                const payment_amount =
                  (principal * (r * Math.pow(1 + r, n))) /
                  (Math.pow(1 + r, n) - 1);
                const total_payment = payment_amount * n;
                interestAmount = total_payment - principal;
              } else {
                interestAmount = principal * (interestRate / 100);
              }
            } else {
              interestAmount = principal * (interestRate / 100);
            }
          }
        }
      } else {
        // Default to FV formula for simple interest
        const loanTerm = parseFloat(
          loan.loan_term ?? loanProduct.loan_term ?? 1,
        );
        const expectedReturn =
          principal * (1 + (interestRate / 100) * loanTerm);
        interestAmount = expectedReturn - principal;
      }

      // Ensure result is valid
      if (isNaN(interestAmount) || interestAmount < 0) {
        interestAmount = 0;
      }
    } catch (error) {
      console.warn("Error calculating interest for loan:", loan.id, error);
      interestAmount = 0;
    }

    return Math.round(interestAmount * 100) / 100;
  })();

  // Calculate payment progress for loan only (not including debts)
  const paymentProgress = loan
    ? Math.round((loan.paid_amount / loan.amount) * 100)
    : 0;

  // Calculate remaining balance including all components
  // Note: totalOutstandingDebt already accounts for debt payments (amount_owing - amount_paid)
  // So we don't deduct loan payments from outstanding debts
  const remainingBalance = loan
    ? Math.round(
        (loan.amount +
          expectedInterest +
          gpsFees -
          (loan.paid_amount || 0) +
          totalOutstandingDebt) *
          100,
      ) / 100
    : 0;

  const paymentData = {
    labels: payments.map((p) =>
      new Date(p.transaction_date).toLocaleDateString(),
    ),
    datasets: [
      {
        label: "Payment Amount (ZMW)",
        data: payments.map((p) => p.amount),
        backgroundColor: "#1890ff",
      },
    ],
  };

  const paymentSchedule = (() => {
    if (!loan) return [];
    if (Array.isArray(loan.payment_schedule) && loan.payment_schedule.length) {
      return loan.payment_schedule;
    }
    if (!loan.loan_product?.auto_split_payments) return [];

    const scheduleLoanProduct = {
      ...loan.loan_product,
      loan_term: loan.loan_term ?? loan.loan_product?.loan_term,
    };
    const scheduleResult = calculateLoanPayments(
      scheduleLoanProduct,
      loan.amount,
      loan.approval_date ? new Date(loan.approval_date) : new Date(),
    );

    return scheduleResult.payment_schedule || [];
  })();

  const collateralColumns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">
            Value: ZMW {record.valuation_amount?.toLocaleString()}
          </Text>
        </div>
      ),
    },
    {
      title: "Collateral",
      dataIndex: "documents",
      key: "documents",
      render: (docs) => (
        <Image.PreviewGroup>
          {docs?.map((url, i) => (
            <Image
              key={i}
              src={url}
              width={50}
              height={50}
              style={{ marginRight: 8, borderRadius: 4 }}
            />
          ))}
        </Image.PreviewGroup>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status ? status.toUpperCase() : "UNKNOWN"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => navigate(`/collateral/${record.id}`)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const scheduleColumns = [
    {
      title: "#",
      dataIndex: "installment",
      key: "installment",
      width: 60,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      render: (value) => (value ? new Date(value).toLocaleDateString() : "N/A"),
    },
    {
      title: "Amount (ZMW)",
      dataIndex: "amount",
      key: "amount",
      render: (value) =>
        value !== null && value !== undefined
          ? `ZMW ${Number(value).toLocaleString()}`
          : "N/A",
    },
    {
      title: "Total Amt Loaned",
      dataIndex: "principal",
      key: "principal",
      render: (value) =>
        value !== null && value !== undefined
          ? `ZMW ${Number(value).toLocaleString()}`
          : "-",
    },
    {
      title: "Interest",
      dataIndex: "interest",
      key: "interest",
      render: (value) =>
        value !== null && value !== undefined
          ? `ZMW ${Number(value).toLocaleString()}`
          : "-",
    },
    {
      title: "Balance",
      dataIndex: "remaining_balance",
      key: "remaining_balance",
      render: (value) =>
        value !== null && value !== undefined
          ? `ZMW ${Number(value).toLocaleString()}`
          : "-",
    },
  ];

  const handleCreateDebt = async (values) => {
    try {
      setSubmittingDebt(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/debts",
        {
          debtor_id: loan.borrower.id,
          loan_id: id,
          amount_owing: values.amount_owing,
          amount_paid: 0,
          debt_type: values.debt_type,
          description: values.description,
          date_created: new Date().toISOString(),
          due_date: values.due_date
            ? values.due_date.format("YYYY-MM-DD")
            : null,
          status: "active",
          created_by: user.id,
          company: company.id,
          branch: company.branch || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setDebts([response.data.data, ...debts]);
      message.success("Outstanding debt recorded successfully!");
      debtForm.resetFields();
      setShowDebtModal(false);
    } catch (error) {
      message.error("Failed to record debt: " + error.message);
    } finally {
      setSubmittingDebt(false);
    }
  };

  const handleUploadSchedule = async () => {
    if (!scheduleFile) {
      message.error("Please select a CSV file to upload");
      return;
    }

    try {
      setUploadingSchedule(true);
      const token = localStorage.getItem("token");

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const lines = text.split("\n").filter((line) => line.trim());

          if (lines.length < 2) {
            message.error("CSV file is empty or invalid");
            return;
          }

          // Parse CSV header
          const headers = lines[0]
            .split(",")
            .map((h) => h.trim().toLowerCase());
          const requiredFields = [
            "month",
            "due_date",
            "amount_due",
            "interest_rate",
            "expected_amount",
            "profit",
            "status",
          ];

          const missingFields = requiredFields.filter(
            (f) => !headers.includes(f),
          );
          if (missingFields.length > 0) {
            message.error(
              `Missing required fields: ${missingFields.join(", ")}`,
            );
            return;
          }

          // Parse data rows
          const scheduleEntries = [];
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(",").map((v) => v.trim());
            const entry = {};
            headers.forEach((header, index) => {
              entry[header] = values[index] || "";
            });

            // Skip empty rows
            if (!entry.month || !entry.amount_due) continue;

            scheduleEntries.push({
              loan_reference: id,
              borrower_nrc: loan.borrower?.nrc || "",
              month: entry.month,
              due_date: entry.due_date,
              amount_due: parseFloat(entry.amount_due) || 0,
              interest_rate: parseFloat(entry.interest_rate) || 0,
              expected_amount: parseFloat(entry.expected_amount) || 0,
              profit: parseFloat(entry.profit) || 0,
              status: entry.status || "pending",
              sheet: entry.sheet || entry.month,
            });
          }

          if (scheduleEntries.length === 0) {
            message.error("No valid schedule entries found in CSV");
            return;
          }

          // Upload to Directus
          const response = await axios.post(
            "https://zedlenders.pickmesms.com/items/amortization",
            scheduleEntries,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          message.success(
            `Successfully uploaded ${scheduleEntries.length} schedule entries!`,
          );
          setShowUploadScheduleModal(false);
          setScheduleFile(null);

          // Refresh amortization schedule
          const res = await axios.get(
            `https://zedlenders.pickmesms.com/items/amortization`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                "filter[loan_reference][_eq]": id,
                fields: "*",
                sort: "due_date",
                limit: -1,
              },
            },
          );
          setAmortizationSchedule(res.data.data || []);
        } catch (error) {
          console.error("Upload error:", error);
          message.error(
            "Failed to upload schedule: " +
              (error.response?.data?.errors?.[0]?.message || error.message),
          );
        } finally {
          setUploadingSchedule(false);
        }
      };

      reader.readAsText(scheduleFile);
    } catch (error) {
      message.error("Failed to read file: " + error.message);
      setUploadingSchedule(false);
    }
  };

  const handleUploadSingleSchedule = async (values) => {
    try {
      setUploadingSchedule(true);
      const token = localStorage.getItem("token");

      const scheduleEntry = {
        loan_reference: id,
        borrower_nrc: loan.borrower?.nrc || "",
        month: values.month,
        due_date: values.due_date.format("YYYY-MM-DD"),
        amount_due: parseFloat(values.amount_due) || 0,
        interest_rate: parseFloat(values.interest_rate) || 0,
        expected_amount: parseFloat(values.expected_amount) || 0,
        profit: parseFloat(values.profit) || 0,
        status: values.status || "pending",
        sheet: values.month,
      };

      await axios.post(
        "https://zedlenders.pickmesms.com/items/amortization",
        scheduleEntry,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      message.success("Schedule entry added successfully!");
      setShowUploadScheduleModal(false);
      scheduleForm.resetFields();

      // Refresh amortization schedule
      const res = await axios.get(
        `https://zedlenders.pickmesms.com/items/amortization`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            "filter[loan_reference][_eq]": id,
            fields: "*",
            sort: "due_date",
            limit: -1,
          },
        },
      );
      setAmortizationSchedule(res.data.data || []);
    } catch (error) {
      console.error("Upload error:", error);
      message.error(
        "Failed to add schedule entry: " +
          (error.response?.data?.errors?.[0]?.message || error.message),
      );
    } finally {
      setUploadingSchedule(false);
    }
  };

  const openEditInstallment = (record) => {
    setEditingInstallment(record);
    editScheduleForm.setFieldsValue({
      month: record.month,
      due_date: record.due_date ? moment(record.due_date) : null,
      amount_due: record.amount_due,
      interest_rate: record.interest_rate,
      expected_amount: record.expected_amount,
      profit: record.profit,
      status: record.status || "pending",
    });
  };

  const handleSaveInstallment = async (values) => {
    if (!editingInstallment) return;
    try {
      setSavingEdit(true);
      const token = localStorage.getItem("token");

      const payload = {
        month: values.month,
        due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
        amount_due: parseFloat(values.amount_due) || 0,
        interest_rate: parseFloat(values.interest_rate) || 0,
        expected_amount: parseFloat(values.expected_amount) || 0,
        profit: parseFloat(values.profit) || 0,
        status: values.status || "pending",
        sheet: values.month,
      };

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/amortization/${editingInstallment.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      message.success("Installment updated successfully!");

      const res = await axios.get(
        `https://zedlenders.pickmesms.com/items/amortization`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            "filter[loan_reference][_eq]": id,
            fields: "*",
            sort: "due_date",
            limit: -1,
          },
        },
      );
      setAmortizationSchedule(res.data.data || []);

      setEditingInstallment(null);
      editScheduleForm.resetFields();
    } catch (error) {
      console.error("Edit error:", error);
      message.error(
        "Failed to update installment: " +
          (error.response?.data?.errors?.[0]?.message || error.message),
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteInstallment = async (installmentId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/amortization/${installmentId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      message.success("Installment deleted");
      setAmortizationSchedule((prev) =>
        prev.filter((inst) => inst.id !== installmentId),
      );
    } catch (error) {
      console.error("Delete error:", error);
      message.error(
        "Failed to delete installment: " +
          (error.response?.data?.errors?.[0]?.message || error.message),
      );
    }
  };

  const handleCreatePtp = async (values) => {
    try {
      setSubmittingPtp(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/ptps",
        {
          borrower: loan.borrower.id,
          amount_promised: values.amount,
          date_promised: values.date_promised.format("YYYY-MM-DD"),
          reminder_date: values.reminder_date.format("YYYY-MM-DD"),
          reminder_text: values.reminder_text,
          loan_id: id, // Add this if your backend supports loan association
          company: company.id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setPtps([response.data.data, ...ptps]);
      message.success("Promise to Pay added successfully!");
      ptpForm.resetFields();
      setShowPtpModal(false);
    } catch (error) {
      message.error("Failed to add Promise to Pay: " + error.message);
    } finally {
      setSubmittingPtp(false);
    }
  };

  const handleAssignGpsTracker = async (values) => {
    try {
      setSubmittingGps(true);
      const token = localStorage.getItem("token");

      // Update the GPS tracker with loan assignment
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${values.tracker_id}`,
        {
          assigned_loan: id,
          status: "active",
          issued_to: loan.borrower.id,
          date_issued: new Date().toISOString(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Refresh the data
      const [gpsTrackersRes, availableTrackersRes] = await Promise.all([
        axios.get(
          `https://zedlenders.pickmesms.com/items/gps_trackers?filter[assigned_loan]=${id}&filter[company]=${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.get(
          `https://zedlenders.pickmesms.com/items/gps_trackers?filter[status]=not_assigned&filter[company]=${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);

      setGpsTrackers(gpsTrackersRes.data.data);
      setAvailableTrackers(availableTrackersRes.data.data);

      message.success("GPS tracker assigned successfully!");
      gpsForm.resetFields();
      setShowGpsModal(false);

      // Navigate to trackers page
      navigate("/trackers");
    } catch (error) {
      message.error("Failed to assign GPS tracker: " + error.message);
    } finally {
      setSubmittingGps(false);
    }
  };

  const handleRemoveGpsTracker = async (trackerId) => {
    try {
      const token = localStorage.getItem("token");

      // Update the GPS tracker to remove loan assignment
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${trackerId}`,
        {
          assigned_loan: null,
          status: "not_assigned",
          issued_to: null,
          date_issued: null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Refresh the data
      const [gpsTrackersRes, availableTrackersRes] = await Promise.all([
        axios.get(
          `https://zedlenders.pickmesms.com/items/gps_trackers?filter[assigned_loan]=${id}&filter[company]=${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.get(
          `https://zedlenders.pickmesms.com/items/gps_trackers?filter[status]=not_assigned&filter[company]=${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);

      setGpsTrackers(gpsTrackersRes.data.data);
      setAvailableTrackers(availableTrackersRes.data.data);

      message.success("GPS tracker removed successfully!");
    } catch (error) {
      message.error("Failed to remove GPS tracker: " + error.message);
    }
  };

  const generateLoanStatement = () => {
    const doc = new jsPDF();
    const logoUrl = "https://example.com/your-logo.png"; // Add your logo URL

    // Header Section
    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.setFont("helvetica", "bold");
    doc.text("LOAN STATEMENT", 105, 20, { align: "center" });

    doc.setFontSize(12);
    doc.setTextColor(100);
    //doc.text(`Loan ID: #${loan.id}`, 15, 30);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 15, 35);

    // Borrower Information
    doc.setFontSize(14);
    doc.setTextColor(40);
    doc.text("Borrower Details", 15, 45);
    doc.setFontSize(12);
    doc.text(
      `Name: ${loan.borrower.first_name + " " + loan.borrower.last_name}`,
      15,
      55,
    );
    doc.text(
      `Loan Date: ${new Date(loan.date_created).toLocaleDateString()}`,
      15,
      60,
    );
    doc.text(`Status: ${statusMap[loan.loan_status]?.text}`, 15, 65);

    // Summary Section
    const summaryData = [
      ["Total Amount", `ZMW ${loan.amount.toLocaleString()}`],
      ...(gpsFees > 0
        ? [["GPS Tracker Fees", `ZMW ${gpsFees.toLocaleString()}`]]
        : []),
      ...(totalOutstandingDebt > 0
        ? [
            [
              "Outstanding Debts",
              `ZMW ${totalOutstandingDebt.toLocaleString()}`,
            ],
          ]
        : []),
      ["Amount Paid", `ZMW ${loan.paid_amount?.toLocaleString() || 0}`],
      ["Remaining Balance", `ZMW ${remainingBalance.toLocaleString()}`],
      ["Interest Rate", `${getEffectiveInterestRate(loan)}%`],
      [
        "Collateral Value",
        `ZMW ${collateral
          .reduce((sum, c) => sum + (c.valuation_amount || 0), 0)
          .toLocaleString()}`,
      ],
    ];

    doc.autoTable({
      startY: 75,
      head: [["Summary Item", "Value"]],
      body: summaryData,
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { font: "helvetica", fontSize: 10 },
    });

    // Payment History Table
    const paymentData = payments.map((p) => [
      new Date(p.transaction_date).toLocaleDateString(),
      `ZMW ${(p?.amount || 0).toLocaleString()}`,
      p.payment_method,
      p.reference_number,
      p.transaction_status ? p.transaction_status.toUpperCase() : "UNKNOWN",
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [["Date", "Amount", "Method", "Reference", "Status"]],
      body: paymentData,
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { font: "helvetica", fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 45 },
        4: { cellWidth: 25 },
      },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(
        `Confidential Document - Generated by ${company_name}`,
        105,
        285,
        {
          align: "center",
        },
      );
    }

    doc.save(`Loan-Statement-${loan.id}.pdf`);
  };

  // Loan deletion handlers
  const handleDeleteLoan = async () => {
    try {
      // Get deletion preview
      const preview = await LoanService.getLoanDeletionPreview(id, token);
      setDeletionPreview(preview);
      setShowDeleteModal(true);
    } catch (error) {
      console.error("Error getting deletion preview:", error);
      message.error("Failed to load deletion preview");
    }
  };

  const confirmDeleteLoan = async (values) => {
    try {
      setDeletingLoan(true);

      const result = await LoanService.deleteLoan(
        id,
        token,
        company.id,
        values.refund_account_id,
      );

      if (result.success) {
        message.success(result.message);

        // Navigate to loans status page and trigger refresh
        setTimeout(() => {
          navigate("/loans/7");
          // Force a page refresh to update accounts
          window.location.reload();
        }, 1500);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error("Loan deletion error:", error);
      message.error("Failed to delete loan");
    } finally {
      setDeletingLoan(false);
      setShowDeleteModal(false);
      deleteForm.resetFields();
    }
  };

  const handleIncrementLoan = async (values) => {
    setSubmittingIncrement(true);
    try {
      const incrementAmount = parseFloat(values.increment_amount);
      const incrementDetails = calculateIncrementDetails(incrementAmount);

      if (!incrementDetails) {
        throw new Error("Invalid increment amount");
      }

      // Update the loan with new amounts
      const updatedLoanData = {
        amount: incrementDetails.newPrincipal,
        interest_amount: incrementDetails.newInterestAmount,
        total_amount_due: incrementDetails.newTotalAmount,
        // Keep all other fields the same (due dates, fees, etc.)
      };

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${loan.id}`,
        updatedLoanData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Update the source account balance (debit the increment amount)
      const sourceAccount = accounts.find(
        (acc) => acc.id === values.from_account,
      );
      if (sourceAccount) {
        const currentBalance = parseFloat(sourceAccount.balance || 0);
        const newBalance = currentBalance - incrementAmount;

        console.log("🔍 Account Balance Update Debug:", {
          accountName: sourceAccount.name,
          currentBalance,
          incrementAmount,
          newBalance,
          operation: "DEBIT (subtracting increment amount)",
        });

        if (newBalance < 0) {
          throw new Error(
            `Insufficient funds in ${sourceAccount.name}. Available: ZMW ${currentBalance.toLocaleString()}, Required: ZMW ${incrementAmount.toLocaleString()}`,
          );
        }

        await axios.patch(
          `https://zedlenders.pickmesms.com/items/accounts/${values.from_account}`,
          { balance: newBalance },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        // Update the account balance in the context
        updateAccountBalance(values.from_account, newBalance);

        console.log("✅ Account balance updated successfully");
      }

      // Create a transaction record for the increment
      // Note: We don't include from_account in the transaction to avoid backend auto-processing
      // The account balance has already been updated manually above
      await axios.post(
        `https://zedlenders.pickmesms.com/items/transactions`,
        {
          amount: incrementAmount,
          transaction_date: new Date().toISOString().split("T")[0],
          company: company.id,
          loan: loan.id,
          // from_account: values.from_account, // Removed to prevent backend auto-processing
          is_loan_transaction: true,
          transaction_status: "Completed",
          transaction_type: "Loan Increment",
          notes: `Loan increment of ZMW ${incrementAmount.toLocaleString()} from ${sourceAccount?.name || "selected account"}. Principal increased from ZMW ${incrementDetails.currentPrincipal.toLocaleString()} to ZMW ${incrementDetails.newPrincipal.toLocaleString()}. Interest recalculated from ZMW ${incrementDetails.currentInterest.toLocaleString()} to ZMW ${incrementDetails.newInterestAmount.toLocaleString()}. Account ${sourceAccount?.name} debited by ZMW ${incrementAmount.toLocaleString()}.`,
          payment_method: values.payment_method || "cash",
          reference_number: `LOAN-INCREMENT-${loan.id}-${Date.now()}`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      message.success("Loan increment processed successfully!");
      setShowIncrementModal(false);
      incrementForm.resetFields();
      setIncrementCalculations(null);

      // Refresh the loan data
      window.location.reload();
    } catch (error) {
      console.error("Loan increment error:", error);
      message.error("Failed to process loan increment: " + error.message);
    } finally {
      setSubmittingIncrement(false);
    }
  };

  const handleLoanAdjustment = async (values) => {
    setSubmittingAdjustment(true);
    try {
      const token = localStorage.getItem("token");

      // Prepare adjustment data (without notes first)
      const adjustmentData = {
        ...(values.approval_date && {
          approval_date: values.approval_date.format("YYYY-MM-DD"),
        }),
        ...(values.disbursement_date && {
          disbursement_date: values.disbursement_date.format("YYYY-MM-DD"),
        }),
        ...(values.next_payment_date && {
          next_payment_date: values.next_payment_date.format("YYYY-MM-DD"),
        }),
        ...(values.amount !== undefined && {
          amount: parseFloat(values.amount),
        }),
        ...(values.interest_amount !== undefined && {
          interest_amount: parseFloat(values.interest_amount),
        }),
        ...(values.total_amount_due !== undefined && {
          total_amount_due: parseFloat(values.total_amount_due),
        }),
        ...(values.allow_custom_interest_rate !== undefined && {
          allow_custom_interest_rate: values.allow_custom_interest_rate,
        }),
        ...(values.custom_interest_rate !== undefined && {
          custom_interest_rate: parseFloat(values.custom_interest_rate),
        }),
        ...(values.interest_applied !== undefined && {
          interest_applied: parseFloat(values.interest_applied),
        }),
      };

      // Add adjustment notes to the loan's notes field (after adjustmentData is defined)
      if (values.adjustment_notes) {
        adjustmentData.notes =
          `${loan.notes || ""}\n\n[ADMIN ADJUSTMENT - ${moment().format("DD MMM YYYY HH:mm")}]\n${values.adjustment_notes}\nChanges: ${JSON.stringify(adjustmentData, null, 2)}`.trim();
      }

      console.log("💰 Loan Adjustment:", adjustmentData);

      // Update the loan
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${loan.id}`,
        adjustmentData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      message.success("Loan adjusted successfully!");
      setShowAdjustmentModal(false);
      adjustmentForm.resetFields();

      // Refresh the loan data
      window.location.reload();
    } catch (error) {
      console.error("Loan adjustment error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      message.error(
        "Failed to adjust loan: " +
          (error.response?.data?.errors?.[0]?.message || error.message),
      );
    } finally {
      setSubmittingAdjustment(false);
    }
  };

  if (loading) {
    return (
      <div className="center-loader">
        <LineWave
          visible={true}
          height="100"
          width="100"
          color="#36454F"
          ariaLabel="line-wave-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Card bordered={false}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="danger">
                Error loading loan details: {error.message}
              </Text>
            }
          >
            <Button type="primary" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  if (!loan) {
    return (
      <div style={{ padding: 24 }}>
        <Card bordered={false}>
          <Empty description={`No loan found with ID: ${id}`}>
            <Button type="primary" onClick={() => navigate("/loans")}>
              View All Loans
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back to Loans
      </Button>

      <Card bordered={false}>
        {/* Header Section */}
        <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
          <Col flex="auto">
            <Title level={3} style={{ marginBottom: 0 }}>
              {loan.borrower.first_name + " " + loan.borrower.last_name}
              <Tag
                color={getCreditScoreInfo(borrowerScore).color}
                style={{ marginLeft: 12, fontSize: "12px" }}
              >
                Credit Score: {borrowerScore}/850
              </Tag>
            </Title>
            <Text type="secondary">
              {loan.borrower_name} • Approved:{" "}
              {new Date(loan.approval_date).toLocaleDateString()}
              {loan.loan_product && (
                <>
                  {" • "}
                  <Tag
                    color="blue"
                    style={{ fontSize: "11px", cursor: "pointer" }}
                    onClick={() => navigate("/loanProducts")}
                  >
                    {loan.loan_product.loan_name}
                  </Tag>
                </>
              )}
            </Text>
          </Col>
          <Col>
            <Tag
              icon={statusMap[loan.loan_status]?.icon}
              color={statusMap[loan.loan_status]?.color}
            >
              {statusMap[loan.loan_status]?.text}
            </Tag>
          </Col>
        </Row>

        {/* Defaulted Loan Alert - Show prominent alert if loan is defaulted and has collateral */}
        {loan.loan_status === "defaulted" && collateral.length > 0 && (
          <Alert
            message="⚠️ Defaulted Loan - Collateral Available for Sale"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  This loan has been defaulted. The following collateral is
                  available for recovery:
                </p>
                <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                  {collateral.map((item) => (
                    <li key={item.id}>
                      <strong>{item.description}</strong> - Valued at K
                      {(item.valuation_amount || 0).toLocaleString()}
                      <Tag
                        color={item.status === "Sold" ? "red" : "orange"}
                        style={{ marginLeft: 8 }}
                      >
                        {item.status || "Active"}
                      </Tag>
                    </li>
                  ))}
                </ul>
                <Space>
                  {collateral
                    .filter((c) => c.status !== "Sold")
                    .map((item) => (
                      <Button
                        key={item.id}
                        type="primary"
                        danger
                        onClick={() => navigate(`/collateral/${item.id}`)}
                        icon={<FireOutlined />}
                      >
                        Sell Collateral: {item.description}
                      </Button>
                    ))}
                </Space>
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card bordered={false} bodyStyle={{ padding: "16px" }}>
              <Statistic
                title={company?.id === 22 ? "Total Amount Loaned" : "Principal"}
                value={jutemMetrics ? jutemMetrics.totalLoaned : loan.amount}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#1890ff", fontSize: 18 }}
                suffix="ZMW"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card bordered={false} bodyStyle={{ padding: "16px" }}>
              <Statistic
                title="Interest"
                value={jutemMetrics ? jutemMetrics.interest : expectedInterest}
                prefix={<LineChartOutlined />}
                valueStyle={{ color: "#52c41a", fontSize: 18 }}
                formatter={(value) => `${Math.round(value).toLocaleString()}`}
                suffix="ZMW"
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  @ {getEffectiveInterestRate(loan).toFixed(2)}%
                </Text>
                {loan.custom_interest_rate != null &&
                  loan.custom_interest_rate !==
                    loan.loan_product?.interest_rate && (
                    <Tag
                      color="orange"
                      style={{ fontSize: "10px", padding: "0 4px", margin: 0 }}
                    >
                      Custom
                    </Tag>
                  )}
              </div>
            </Card>
          </Col>
          {gpsFees > 0 && (
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card bordered={false} bodyStyle={{ padding: "16px" }}>
                <Statistic
                  title={
                    <GpsFeeTooltip gpsFeesData={gpsFeeData}>
                      GPS Fees
                    </GpsFeeTooltip>
                  }
                  value={gpsFees}
                  prefix={<EnvironmentOutlined />}
                  valueStyle={{ color: "#fa8c16", fontSize: 18 }}
                  suffix="ZMW"
                />
              </Card>
            </Col>
          )}
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card bordered={false} bodyStyle={{ padding: "16px" }}>
              <Statistic
                title="Paid"
                value={jutemMetrics ? jutemMetrics.paid : loan.paid_amount || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a", fontSize: 18 }}
                suffix="ZMW"
              />
              <Progress
                percent={paymentProgress}
                status={loan.loan_status === 8 ? "exception" : "normal"}
                size="small"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card
              bordered={false}
              bodyStyle={{ padding: "16px" }}
              style={{
                borderLeft: "4px solid #722ed1",
                background: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 100%)",
              }}
            >
              <Tooltip
                title={
                  <div style={{ padding: "8px" }}>
                    <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                      Outstanding Breakdown:
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      Principal: ZMW {loan.amount?.toLocaleString()}
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      Interest: ZMW {expectedInterest.toLocaleString()}
                    </div>
                    {gpsFees > 0 && (
                      <div style={{ marginBottom: "4px" }}>
                        GPS Fees: ZMW {gpsFees.toLocaleString()}
                      </div>
                    )}
                    {totalOutstandingDebt > 0 && (
                      <div style={{ marginBottom: "4px" }}>
                        Outstanding Debts: ZMW{" "}
                        {totalOutstandingDebt.toLocaleString()}
                      </div>
                    )}
                    <div
                      style={{
                        marginBottom: "4px",
                        color: "#52c41a",
                      }}
                    >
                      Paid: -ZMW {(loan.paid_amount || 0).toLocaleString()}
                    </div>
                    <div
                      style={{
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid rgba(255,255,255,0.2)",
                        fontWeight: "bold",
                      }}
                    >
                      Total: ZMW {remainingBalance.toLocaleString()}
                    </div>
                  </div>
                }
                overlayStyle={{ maxWidth: "300px" }}
              >
                <div style={{ cursor: "help" }}>
                  <Statistic
                    title={
                      <span>
                        Outstanding{" "}
                        <InfoCircleOutlined
                          style={{
                            fontSize: 12,
                            color: "#722ed1",
                            opacity: 0.6,
                          }}
                        />
                      </span>
                    }
                    value={remainingBalance}
                    prefix={<BankOutlined />}
                    valueStyle={{
                      color: "#722ed1",
                      fontWeight: "bold",
                      fontSize: 20,
                    }}
                    suffix="ZMW"
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {loan.loan_status === 10 ? "Settled" : "Amount remaining"}
                  </Text>
                </div>
              </Tooltip>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Card bordered={false} bodyStyle={{ padding: "16px" }}>
              <Statistic
                title="Next Payment"
                value={
                  loan.loan_status === 10
                    ? "Settled"
                    : loan.next_payment_date
                      ? moment(loan.next_payment_date).format("DD MMM YY")
                      : "N/A"
                }
                prefix={<CalendarOutlined />}
                valueStyle={{
                  color: loan.loan_status === 10 ? "#52c41a" : "#1890ff",
                  fontSize: 16,
                }}
              />
              {loan.next_payment_date && loan.loan_status !== 10 && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {Math.ceil(
                    (new Date(loan.next_payment_date) - new Date()) /
                      (1000 * 3600 * 24),
                  )}{" "}
                  days
                </Text>
              )}
            </Card>
          </Col>
        </Row>

        {/* Tabs for Detailed Sections */}
        <Tabs defaultActiveKey={company?.id === 22 ? "schedule" : "1"}>
          <TabPane tab="📊 Loan Overview" key="1">
            <Row gutter={24}>
              <Col span={16}>
                <Card title="Payment History" style={{ marginBottom: 24 }}>
                  <Bar
                    data={paymentData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" },
                        title: { display: true, text: "Payment Timeline" },
                      },
                    }}
                  />
                </Card>

                <Card title="Transaction Timeline">
                  <Timeline mode="alternate">
                    {payments.map((payment) => (
                      <Timeline.Item
                        key={payment.id}
                        color={
                          payment.transaction_status === "Completed"
                            ? "green"
                            : payment.transaction_status === "Failed"
                              ? "red"
                              : "orange"
                        }
                        label={new Date(
                          payment.transaction_date,
                        ).toLocaleDateString()}
                      >
                        <div className="payment-card">
                          <Text strong style={{ fontSize: 16 }}>
                            ZMW {payment.amount.toLocaleString()}
                          </Text>
                          <div style={{ marginTop: 8 }}>
                            <Tag
                              color={
                                payment.transaction_status === "Completed"
                                  ? "green"
                                  : payment.transaction_status === "failed"
                                    ? "red"
                                    : "orange"
                              }
                            >
                              {payment.transaction_status}
                            </Tag>
                            <Text type="secondary">
                              {payment.transaction_type}
                            </Text>
                          </div>
                          {payment.notes && (
                            <Text
                              type="secondary"
                              style={{ marginTop: 8, display: "block" }}
                            >
                              <FileTextOutlined /> {payment.notes}
                            </Text>
                          )}
                          {payment.reference_number && (
                            <Text
                              code
                              style={{ marginTop: 8, display: "block" }}
                            >
                              Ref: {payment.reference_number}
                            </Text>
                          )}

                          {/* Reversal Button - Admin Only */}
                          {/* {user?.role === "Administrator" &&
                            !payment.is_reversed &&
                            !payment.is_reversal &&
                            payment.transaction_status === "Completed" && (
                              <div style={{ marginTop: 8 }}>
                                <Button
                                  type="link"
                                  size="small"
                                  danger
                                  icon={<UndoOutlined />}
                                  onClick={() => {
                                    setSelectedTransactionForReversal(payment);
                                    setShowReversalModal(true);
                                  }}
                                  style={{ padding: 0, height: "auto" }}
                                >
                                  Reverse
                                </Button>
                              </div>
                            )} */}

                          {/* Show if transaction was reversed */}
                          {payment.is_reversed && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="orange" size="small">
                                REVERSED
                              </Tag>
                              {payment.reversed_date && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "11px", marginLeft: 4 }}
                                >
                                  on{" "}
                                  {new Date(
                                    payment.reversed_date,
                                  ).toLocaleDateString()}
                                </Text>
                              )}
                            </div>
                          )}

                          {/* Show if this is a reversal transaction */}
                          {payment.is_reversal && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="blue" size="small">
                                REVERSAL ENTRY
                              </Tag>
                            </div>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Card>
              </Col>
              <Col span={8}>
                <Card title="Quick Actions" style={{ marginBottom: 24 }}>
                  {loan.loan_status !== 10 ? (
                    <Button
                      block
                      type="primary"
                      onClick={() => setShowPaymentModal(true)}
                      style={{ marginBottom: 8 }}
                    >
                      Record Payment
                    </Button>
                  ) : (
                    <></>
                  )}
                  {loan.loan_status === 7 && (
                    <Button
                      block
                      type="default"
                      onClick={() => {
                        setIncrementCalculations(null);
                        setShowIncrementModal(true);
                      }}
                      style={{
                        marginBottom: 8,
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                        color: "white",
                      }}
                      icon={<DollarOutlined />}
                    >
                      Increment Loan
                    </Button>
                  )}
                  {loan.loan_status === 7 && (
                    <Button
                      block
                      type="default"
                      onClick={handleReloan}
                      style={{
                        marginBottom: 8,
                        backgroundColor: "#fa8c16",
                        borderColor: "#fa8c16",
                        color: "white",
                      }}
                      icon={<SyncOutlined />}
                    >
                      Manual Reloan
                    </Button>
                  )}
                  <Button
                    block
                    onClick={() => setShowPtpModal(true)}
                    style={{ marginBottom: 8 }}
                    icon={<ScheduleOutlined />}
                  >
                    Record PTP
                  </Button>
                  <Button
                    block
                    onClick={() => navigate(`/updateLoanStatus/${id}`)}
                    style={{ marginBottom: 8 }}
                    icon={<FileTextOutlined />}
                  >
                    Update Status
                  </Button>
                  {user?.role === "Administrator" && (
                    <Button
                      block
                      onClick={() => {
                        adjustmentForm.setFieldsValue({
                          approval_date: loan.approval_date
                            ? moment(loan.approval_date)
                            : null,
                          disbursement_date: loan.disbursement_date
                            ? moment(loan.disbursement_date)
                            : null,
                          amount: loan.amount,
                          interest_amount: loan.interest_amount,
                          total_amount_due: loan.total_amount_due,
                        });
                        setShowAdjustmentModal(true);
                      }}
                      style={{
                        marginBottom: 8,
                        backgroundColor: "#faad14",
                        borderColor: "#faad14",
                        color: "white",
                      }}
                      icon={<AlertOutlined />}
                    >
                      Admin Adjust Loan
                    </Button>
                  )}
                  <Button
                    block
                    onClick={() => setShowGpsModal(true)}
                    style={{ marginBottom: 8 }}
                    icon={<EnvironmentOutlined />}
                  >
                    Assign GPS Tracker
                  </Button>
                  {company?.id === 19 ? (
                    <>
                      {/* <LoanAgreementGenerator
                        loan={loan}
                        borrower={loan.borrower}
                        nextOfKin={
                          loan?.borrower?.next_kin_name ||
                          loan?.borrower?.next_kin_phone ||
                          loan?.borrower?.next_kin_relationship
                            ? {
                                names: loan?.borrower?.next_kin_name,
                                phone: loan?.borrower?.next_kin_phone,
                                relationship:
                                  loan?.borrower?.next_kin_relationship,
                              }
                            : null
                        }
                        buttonProps={{
                          block: true,
                          type: "default",
                          style: { marginBottom: 8 },
                        }}
                        showModal={true}
                      /> */}
                      <LoanStatementGenerator
                        loan={loan}
                        borrower={loan.borrower}
                        transactions={payments}
                        buttonProps={{
                          block: true,
                          type: "default",
                          style: { marginBottom: 8 },
                        }}
                        showModal={true}
                      />
                    </>
                  ) : company?.id === 22 ? (
                    <JutemStatementGenerator
                      loan={loan}
                      borrower={loan.borrower}
                      transactions={payments}
                      amortizationSchedule={amortizationSchedule}
                      buttonProps={{
                        block: true,
                        type: "default",
                        style: { marginBottom: 8 },
                      }}
                    />
                  ) : (
                    <Button
                      block
                      onClick={generateLoanStatement}
                      style={{ marginBottom: 8 }}
                    >
                      Generate Statement
                    </Button>
                  )}
                  <Button
                    block
                    onClick={() => setShowDebtModal(true)}
                    style={{ marginBottom: 8 }}
                    icon={<DollarOutlined />}
                  >
                    Record Outstanding Debt
                  </Button>
                  <Button
                    block
                    danger
                    onClick={() => setShowFlagIssueModal(true)}
                    icon={<ExclamationCircleOutlined />}
                  >
                    Flag Issues
                  </Button>
                  {canDelete && (
                    <Button
                      block
                      danger
                      type="primary"
                      onClick={handleDeleteLoan}
                      icon={<DeleteOutlined />}
                      style={{
                        marginTop: 8,
                        backgroundColor: "#ff4d4f",
                        borderColor: "#ff4d4f",
                      }}
                    >
                      Delete Loan
                    </Button>
                  )}
                </Card>

                <Card
                  title="Collateral Summary"
                  extra={
                    <Button
                      type="link"
                      size="small"
                      onClick={() => navigate("/collateral")}
                    >
                      Manage All Collateral
                    </Button>
                  }
                >
                  <Table
                    columns={collateralColumns}
                    dataSource={collateral}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="📅 Payment Schedule" key="schedule">
            {company?.id === 22 ? (
              <Card
                title="Amortization Schedule"
                extra={
                  <Space>
                    <Text type="secondary">
                      {jutemMetrics && jutemMetrics.pending.length > 0
                        ? `${jutemMetrics.pending.length} not paid installments`
                        : "No not paid installments"}
                    </Text>
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={() => setShowUploadScheduleModal(true)}
                    >
                      Upload Schedule
                    </Button>
                  </Space>
                }
              >
                {jutemMetrics && jutemMetrics.pending.length > 0 ? (
                  <Table
                    columns={[
                      {
                        title: "#",
                        key: "index",
                        width: 60,
                        render: (_, __, idx) => idx + 1,
                      },
                      {
                        title: "Month",
                        dataIndex: "month",
                        key: "month",
                      },
                      {
                        title: "Due Date",
                        dataIndex: "due_date",
                        key: "due_date",
                        render: (v) =>
                          v ? new Date(v).toLocaleDateString() : "N/A",
                      },
                      {
                        title: "Principal (ZMW)",
                        dataIndex: "amount_due",
                        key: "amount_due",
                        render: (v) =>
                          `ZMW ${parseFloat(v || 0).toLocaleString()}`,
                      },
                      {
                        title: "Interest Rate (%)",
                        dataIndex: "interest_rate",
                        key: "interest_rate",
                        render: (v) => `${parseFloat(v || 0)}%`,
                      },
                      {
                        title: "Expected Amount (ZMW)",
                        dataIndex: "expected_amount",
                        key: "expected_amount",
                        render: (v) =>
                          `ZMW ${parseFloat(v || 0).toLocaleString()}`,
                      },
                      {
                        title: "Profit (ZMW)",
                        dataIndex: "profit",
                        key: "profit",
                        render: (v) =>
                          `ZMW ${parseFloat(v || 0).toLocaleString()}`,
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (v) => {
                          const raw = (v || "pending").toLowerCase();
                          const label =
                            raw === "pending" ? "NOT PAID" : raw.toUpperCase();
                          const color = ["paid", "cleared"].includes(raw)
                            ? "green"
                            : raw === "pending"
                              ? "orange"
                              : "red";
                          return <Tag color={color}>{label}</Tag>;
                        },
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        fixed: "right",
                        width: 140,
                        render: (_, record) => (
                          <Space size="small">
                            <Button
                              type="primary"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => openEditInstallment(record)}
                            >
                              Edit
                            </Button>
                            <Popconfirm
                              title="Delete this installment?"
                              description="This cannot be undone."
                              onConfirm={() =>
                                handleDeleteInstallment(record.id)
                              }
                              okText="Delete"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                              />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={jutemMetrics ? jutemMetrics.pending : []}
                    rowKey="id"
                    pagination={{ pageSize: 12 }}
                    size="small"
                    scroll={{ x: true }}
                  />
                ) : (
                  <Empty description="No not paid installments for this loan." />
                )}
              </Card>
            ) : (
              <Card
                title="Amortization Schedule"
                extra={
                  <Text type="secondary">
                    {paymentSchedule.length > 0
                      ? `${paymentSchedule.length} installments`
                      : "No schedule available"}
                  </Text>
                }
              >
                {paymentSchedule.length > 0 ? (
                  <Table
                    columns={scheduleColumns}
                    dataSource={paymentSchedule}
                    rowKey={(record, index) => record.installment || index}
                    pagination={{ pageSize: 12 }}
                    size="small"
                    scroll={{ x: true }}
                  />
                ) : (
                  <Empty description="No payment schedule generated for this loan." />
                )}
              </Card>
            )}
          </TabPane>

          <TabPane tab="🛡️ Collateral" key="2">
            <Card
              title="Photos"
              // extra={
              //   <Button
              //     type="link"
              //     size="small"
              //     onClick={() => navigate('/collateral')}
              //   >
              //     Add New Collateral
              //   </Button>
              // }
            >
              {collateral.length > 0 ? (
                <Image.PreviewGroup>
                  <Row gutter={[16, 16]}>
                    {collateral.flatMap((c) =>
                      c.documents?.map((doc, i) => (
                        <Col span={6} key={`${c.id}-${i}`}>
                          <Card
                            cover={
                              <Image src={doc} alt="Collateral Document" />
                            }
                            hoverable
                            onClick={() => navigate(`/collateral/${c.id}`)}
                            style={{ cursor: "pointer" }}
                            actions={[
                              <Button
                                type="link"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/collateral/${c.id}`);
                                }}
                              >
                                View Details
                              </Button>,
                            ]}
                          >
                            <Text strong>Photo {i + 1}</Text>
                            <br />
                            <Text type="secondary">{c.description}</Text>
                          </Card>
                        </Col>
                      )),
                    )}
                  </Row>
                </Image.PreviewGroup>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No collateral documents available"
                >
                  <Button
                    type="primary"
                    icon={<PictureOutlined />}
                    onClick={() => setShowUploadModal(true)}
                  >
                    Upload Documents
                  </Button>
                </Empty>
              )}
            </Card>
          </TabPane>

          <TabPane tab="🚩 Issues & Risks" key="3">
            <Card
              title="Reported Issues"
              extra={
                <Button
                  type="primary"
                  onClick={() => setShowFlagIssueModal(true)}
                  icon={<ExclamationCircleOutlined />}
                >
                  New Issue
                </Button>
              }
            >
              <Table
                columns={[
                  {
                    title: "Issue",
                    dataIndex: "issue_type",
                    render: (val) =>
                      ISSUE_TYPES.find((t) => t.value === val)?.label,
                  },
                  {
                    title: "Severity",
                    dataIndex: "severity",
                    render: (val) => (
                      <Tag
                        color={
                          val === "critical"
                            ? "red"
                            : val === "high"
                              ? "orange"
                              : val === "medium"
                                ? "yellow"
                                : "green"
                        }
                      >
                        {val ? val.toUpperCase() : "UNKNOWN"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    render: (val) => (
                      <Tag color={val === "resolved" ? "green" : "volcano"}>
                        {val ? val.toUpperCase() : "UNKNOWN"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Reported On",
                    dataIndex: "reported_date",
                    render: (val) =>
                      val ? moment(val).format("DD MMM YYYY") : "N/A",
                  },
                  {
                    title: "Actions",
                    render: (_, record) => (
                      <Button
                        type="link"
                        onClick={() => navigate(`/issues/${record.id}`)}
                      >
                        View Details
                      </Button>
                    ),
                  },
                ]}
                dataSource={existingIssues}
                rowKey="id"
              />
            </Card>
          </TabPane>

          <TabPane tab="💵 Payment Promises" key="4">
            <Card
              title="Promises to Pay (PTP)"
              extra={
                <Button
                  type="primary"
                  onClick={() => setShowPtpModal(true)}
                  icon={<ScheduleOutlined />}
                >
                  New Promise
                </Button>
              }
            >
              <Table
                columns={[
                  {
                    title: "Amount Promised",
                    dataIndex: "amount_promised",
                    render: (val) => `ZMW ${val?.toLocaleString()}`,
                  },
                  {
                    title: "Date Promised",
                    dataIndex: "date_promised",
                    render: (val) => moment(val).format("DD MMM YYYY"),
                  },
                  {
                    title: "Reminder Date",
                    dataIndex: "reminder_date",
                    render: (val) => moment(val).format("DD MMM YYYY"),
                  },
                  {
                    title: "Status",
                    render: (_, record) => (
                      <Tag
                        color={
                          moment(record.date_promised).isAfter(moment())
                            ? "blue"
                            : "red"
                        }
                      >
                        {moment(record.date_promised).isAfter(moment())
                          ? "Pending"
                          : "Overdue"}
                      </Tag>
                    ),
                  },
                ]}
                dataSource={ptps}
                rowKey="id"
              />
            </Card>
          </TabPane>

          <TabPane tab="📍GPS Trackers" key="5">
            <Card
              title="Assigned GPS Trackers"
              extra={
                availableTrackers.length > 0 && (
                  <Button
                    type="primary"
                    onClick={() => setShowGpsModal(true)}
                    icon={<EnvironmentOutlined />}
                  >
                    Assign Tracker
                  </Button>
                )
              }
            >
              {gpsTrackers.length > 0 ? (
                <Table
                  columns={[
                    {
                      title: "GPS Code",
                      dataIndex: "gps_code",
                      key: "gps_code",
                    },
                    {
                      title: "Serial Number",
                      dataIndex: "serial_number",
                      key: "serial_number",
                    },
                    {
                      title: "SIM Number",
                      dataIndex: "sim_number",
                      key: "sim_number",
                    },
                    {
                      title: "Date Issued",
                      dataIndex: "date_issued",
                      key: "date_issued",
                      render: (date) =>
                        date ? moment(date).format("DD MMM YYYY") : "N/A",
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (status) => (
                        <Tag color={status === "active" ? "green" : "orange"}>
                          {status ? status.toUpperCase() : "UNKNOWN"}
                        </Tag>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      render: (_, record) => (
                        <Popconfirm
                          title="Remove GPS Tracker"
                          description="Are you sure you want to remove this GPS tracker from the loan?"
                          onConfirm={() => handleRemoveGpsTracker(record.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button danger size="small" icon={<DeleteOutlined />}>
                            Remove
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                  dataSource={gpsTrackers}
                  rowKey="id"
                  pagination={false}
                />
              ) : (
                <Empty
                  description="No GPS trackers assigned to this loan"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  {availableTrackers.length > 0 && (
                    <Button
                      type="primary"
                      onClick={() => setShowGpsModal(true)}
                      icon={<EnvironmentOutlined />}
                    >
                      Assign First Tracker
                    </Button>
                  )}
                </Empty>
              )}
            </Card>
          </TabPane>

          <TabPane tab="💸 Outstanding Debts" key="6">
            <Card
              title="Outstanding Debts"
              extra={
                <Button
                  type="primary"
                  onClick={() => setShowDebtModal(true)}
                  icon={<DollarOutlined />}
                >
                  Record New Debt
                </Button>
              }
            >
              {debts.length > 0 ? (
                <Table
                  columns={[
                    {
                      title: "Debt Type",
                      dataIndex: "debt_type",
                      key: "debt_type",
                      render: (type) => (
                        <Tag color="orange">
                          {debtTypes.find((t) => t.value === type)?.label ||
                            type}
                        </Tag>
                      ),
                    },
                    {
                      title: "Description",
                      dataIndex: "description",
                      key: "description",
                    },
                    {
                      title: "Amount Owing",
                      dataIndex: "amount_owing",
                      key: "amount_owing",
                      render: (amount) => `ZMW ${amount?.toLocaleString()}`,
                    },
                    {
                      title: "Amount Paid",
                      dataIndex: "amount_paid",
                      key: "amount_paid",
                      render: (amount) =>
                        `ZMW ${(amount || 0).toLocaleString()}`,
                    },
                    {
                      title: "Outstanding",
                      key: "outstanding",
                      render: (_, record) => (
                        <Text strong style={{ color: "#ff4d4f" }}>
                          ZMW{" "}
                          {(
                            record.amount_owing - (record.amount_paid || 0)
                          ).toLocaleString()}
                        </Text>
                      ),
                    },
                    {
                      title: "Date Created",
                      dataIndex: "date_created",
                      key: "date_created",
                      render: (date) => moment(date).format("DD MMM YYYY"),
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (status) => (
                        <Tag color={status === "active" ? "red" : "green"}>
                          {status ? status.toUpperCase() : "UNKNOWN"}
                        </Tag>
                      ),
                    },
                  ]}
                  dataSource={debts}
                  rowKey="id"
                  pagination={false}
                />
              ) : (
                <Empty
                  description="No outstanding debts recorded for this loan"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button
                    type="primary"
                    onClick={() => setShowDebtModal(true)}
                    icon={<DollarOutlined />}
                  >
                    Record First Debt
                  </Button>
                </Empty>
              )}
            </Card>
          </TabPane>
        </Tabs>
      </Card>
      <Modal
        title="Record New Payment"
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handleRecordPayment}
          initialValues={{
            transaction_date: moment(),
            payment_method: "mobile_money",
          }}
        >
          {/* Transaction Type - First field to determine payment type */}
          <Form.Item
            label="Transaction Type"
            name="transaction_type"
            rules={[
              { required: true, message: "Please select transaction type" },
            ]}
          >
            <Select
              options={transactionTypes}
              placeholder="Select payment type first"
            />
          </Form.Item>

          {/* Debt/GPS Selection based on transaction type */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const transactionType = getFieldValue("transaction_type");
              if (transactionType === "Debt Repayment") {
                return (
                  <Form.Item
                    label="Select Debt to Pay"
                    name="debt_id"
                    rules={[
                      {
                        required: true,
                        message: "Please select which debt to pay",
                      },
                    ]}
                  >
                    <Select placeholder="Choose outstanding debt">
                      {debts
                        .filter((debt) => debt.status === "active")
                        .map((debt) => (
                          <Select.Option key={debt.id} value={debt.id}>
                            {debtTypes.find((t) => t.value === debt.debt_type)
                              ?.label || debt.debt_type}{" "}
                            - ZMW{" "}
                            {(
                              debt.amount_owing - (debt.amount_paid || 0)
                            ).toLocaleString()}{" "}
                            outstanding
                            {debt.description &&
                              ` (${debt.description.substring(0, 50)}...)`}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                );
              } else if (transactionType === "GPS Fee Payment") {
                return (
                  <Form.Item
                    label="Select GPS Tracker"
                    name="gps_tracker_id"
                    rules={[
                      {
                        required: true,
                        message: "Please select which GPS tracker fee to pay",
                      },
                    ]}
                  >
                    <Select placeholder="Choose GPS tracker">
                      {gpsTrackers.map((tracker) => (
                        <Select.Option key={tracker.id} value={tracker.id}>
                          GPS: {tracker.gps_code} | Serial:{" "}
                          {tracker.serial_number}
                          {tracker.sim_number &&
                            ` | SIM: ${tracker.sim_number}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          {/* Amortization Installment Selection for company 22 - for Loan Repayment only */}
          {company?.id === 22 && (
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const transactionType = getFieldValue("transaction_type");
                // Only show for Loan Repayment, not for other payment types
                if (
                  transactionType === "Loan Repayment" &&
                  amortizationSchedule.length > 0
                ) {
                  return (
                    <Form.Item
                      label="Select Installment to Pay"
                      name="amortization_id"
                      rules={[
                        {
                          required: true,
                          message: "Please select which installment to pay",
                        },
                      ]}
                      extra="Link this payment to a specific installment in the amortization schedule"
                    >
                      <Select
                        placeholder="Choose installment"
                        onChange={(value) => {
                          // Auto-fill the amount based on selected installment
                          const installment = amortizationSchedule.find(
                            (i) => i.id === value,
                          );
                          if (installment) {
                            paymentForm.setFieldsValue({
                              amount: parseFloat(
                                installment.expected_amount || 0,
                              ),
                            });
                          }
                        }}
                      >
                        {amortizationSchedule
                          .filter(
                            (inst) =>
                              !inst.status ||
                              inst.status.toLowerCase() === "pending",
                          )
                          .map((inst) => (
                            <Select.Option key={inst.id} value={inst.id}>
                              Month {inst.month} - Due:{" "}
                              {new Date(inst.due_date).toLocaleDateString()} -
                              ZMW{" "}
                              {parseFloat(
                                inst.expected_amount || 0,
                              ).toLocaleString()}{" "}
                              {inst.status && `(${inst.status.toUpperCase()})`}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>
          )}

          {/* Dynamic amount field based on transaction type */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const transactionType = getFieldValue("transaction_type");
              const isGpsFeePayment = transactionType === "GPS Fee Payment";
              const isInterestPayment = transactionType === "Interest Payment";
              const isPrincipalPayment =
                transactionType === "Principal Payment";
              const isDiscount = transactionType === "Discount";
              const selectedGpsTracker = getFieldValue("gps_tracker_id");

              let maxAmount = remainingBalance;
              let amountLabel = "Amount (ZMW)";
              let amountMessage = `Amount must be between 1 and ${remainingBalance.toLocaleString()}`;

              // For discount transactions, use loan amount as max (not remaining balance)
              if (isDiscount) {
                maxAmount = parseFloat(loan?.amount || 0);
                amountLabel = `Discount Amount (ZMW) - Max: ${maxAmount.toLocaleString()}`;
                amountMessage = `Discount amount must be between 1 and ${maxAmount.toLocaleString()} (loan amount)`;
              }

              // Calculate outstanding interest
              const calculateOutstandingInterest = () => {
                if (!loan || !loan.loan_product || !loan.amount) return 0;

                // Calculate expected interest using the same logic as elsewhere
                const principal = parseFloat(loan.amount);
                const interestRate = getEffectiveInterestRate(loan);
                const expectedInterest = principal * (interestRate / 100);

                // Get interest payments made (simplified - in reality would need to track separately)
                const interestPayments = payments
                  .filter(
                    (p) =>
                      p.transaction_type === "Interest Payment" &&
                      p.transaction_status === "Completed",
                  )
                  .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

                return (
                  Math.round(
                    Math.max(0, expectedInterest - interestPayments) * 100,
                  ) / 100
                );
              };

              const outstandingInterest = calculateOutstandingInterest();
              const outstandingPrincipal =
                Math.round(
                  Math.max(
                    0,
                    parseFloat(loan?.amount || 0) -
                      parseFloat(loan?.paid_amount || 0),
                  ) * 100,
                ) / 100;

              if (isGpsFeePayment && selectedGpsTracker && gpsFeeData) {
                // Find the specific GPS tracker fee
                const trackerFee = gpsFeeData.trackerFees?.find(
                  (fee) => fee.trackerId === selectedGpsTracker,
                );
                if (trackerFee) {
                  maxAmount = trackerFee.totalFee;
                  amountLabel = `GPS Fee Amount (ZMW) - Max: ${trackerFee.totalFee.toLocaleString()}`;
                  amountMessage = `Amount must be between 1 and ${trackerFee.totalFee}`;
                }
              } else if (isInterestPayment) {
                maxAmount = outstandingInterest;
                amountLabel = `Interest Payment (ZMW) - Max: ${outstandingInterest.toLocaleString()}`;
                amountMessage =
                  outstandingInterest > 0
                    ? `Amount must be between 1 and ${outstandingInterest} (outstanding interest)`
                    : "No outstanding interest to pay";
              } else if (isPrincipalPayment) {
                maxAmount = outstandingPrincipal;
                amountLabel = `Principal Payment (ZMW) - Max: ${outstandingPrincipal.toLocaleString()}`;
                amountMessage =
                  outstandingPrincipal > 0
                    ? `Amount must be between 1 and ${outstandingPrincipal} (outstanding principal)`
                    : "No outstanding principal to pay";
              }

              return (
                <Form.Item
                  label={amountLabel}
                  name="amount"
                  rules={[
                    { required: true, message: "Please enter payment amount" },
                    {
                      type: "number",
                      min: maxAmount > 0 ? 1 : 0,
                      max: maxAmount,
                      message: amountMessage,
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder={
                      maxAmount > 0 ? "Enter amount" : "No amount available"
                    }
                    min={maxAmount > 0 ? 1 : 0}
                    max={maxAmount}
                    step={isGpsFeePayment ? 1 : 100}
                    disabled={maxAmount <= 0}
                    formatter={(value) =>
                      `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Transfer Fees - Only show for non-discount transactions */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const transactionType = getFieldValue("transaction_type");
              const isDiscount = transactionType === "Discount";

              if (isDiscount) return null;

              return (
                <Form.Item
                  label="Transfer Fees (ZMW)"
                  name="transfer_fees"
                  tooltip="Additional fees that will be added to the transaction amount but not counted toward the borrower's payment"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Enter transfer fees (optional)"
                    min={0}
                    step={10}
                    formatter={(value) =>
                      `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Account - Only show for non-discount transactions */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const transactionType = getFieldValue("transaction_type");
              const isDiscount = transactionType === "Discount";

              if (isDiscount) return null;

              return (
                <Form.Item
                  label="Account"
                  name="account_id"
                  rules={[
                    { required: true, message: "Please select an account" },
                  ]}
                >
                  <Select
                    placeholder="Select account to receive payment"
                    showSearch
                    optionFilterProp="children"
                  >
                    {accounts
                      ?.filter(
                        (account) =>
                          account.status === "active" || !account.status,
                      )
                      .map((account) => (
                        <Select.Option key={account.id} value={account.id}>
                          {account.name} - {account.account_type} (Balance: ZMW{" "}
                          {account.balance?.toLocaleString()})
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            label="Payment Date"
            name="transaction_date"
            rules={[{ required: true, message: "Please select payment date" }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              disabledDate={(current) => current > moment()}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Transaction Status"
            name="transaction_status"
            rules={[
              { required: true, message: "Please select transaction status" },
            ]}
          >
            <Select options={transactionStatuses} />
          </Form.Item>

          {/* Payment Method - Only show for non-discount transactions */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const transactionType = getFieldValue("transaction_type");
              const isDiscount = transactionType === "Discount";

              if (isDiscount) return null;

              return (
                <Form.Item
                  label="Payment Method"
                  name="payment_method"
                  rules={[
                    { required: true, message: "Please select payment method" },
                  ]}
                >
                  <Select options={paymentMethods} />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Reference Number - Only show for non-discount transactions */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const transactionType = getFieldValue("transaction_type");
              const isDiscount = transactionType === "Discount";

              if (isDiscount) return null;

              return (
                <Form.Item
                  label="Reference Number"
                  name="reference_number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter reference number",
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="MMF-12345, BANK-67890, etc."
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              rows={3}
              placeholder="Additional payment details..."
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingPayment}
              block
            >
              {submittingPayment ? "Processing..." : "Confirm Payment"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Loan Increment Modal */}
      <Modal
        title="Increment Loan Amount"
        open={showIncrementModal}
        onCancel={() => {
          setShowIncrementModal(false);
          incrementForm.resetFields();
          setIncrementCalculations(null);
        }}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={incrementForm}
          layout="vertical"
          onFinish={handleIncrementLoan}
          onValuesChange={(changedValues, allValues) => {
            if (changedValues.increment_amount) {
              const calculations = calculateIncrementDetails(
                changedValues.increment_amount,
              );
              setIncrementCalculations(calculations);
            }

            // Validate account balance when both amount and account are selected
            if (allValues.increment_amount && allValues.from_account) {
              const selectedAccount = accounts.find(
                (acc) => acc.id === allValues.from_account,
              );
              const incrementAmount = parseFloat(allValues.increment_amount);
              const accountBalance = parseFloat(selectedAccount?.balance || 0);

              if (accountBalance < incrementAmount) {
                incrementForm.setFields([
                  {
                    name: "from_account",
                    errors: [
                      `Insufficient funds. Available: ZMW ${accountBalance.toLocaleString()}, Required: ZMW ${incrementAmount.toLocaleString()}`,
                    ],
                  },
                ]);
              } else {
                incrementForm.setFields([
                  {
                    name: "from_account",
                    errors: [],
                  },
                ]);
              }
            }
          }}
        >
          <Alert
            message="Loan Increment Information"
            description="This will increase the principal amount and recalculate the interest. The increment amount will be debited from the selected account. All other loan terms (due dates, fees, etc.) remain unchanged."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item label="Current Loan Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Current Principal"
                  value={loan?.amount || 0}
                  prefix="ZMW"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Current Interest"
                  value={calculateCurrentInterest()}
                  prefix="ZMW"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>
          </Form.Item>

          <Form.Item
            label="Increment Amount (ZMW)"
            name="increment_amount"
            rules={[
              { required: true, message: "Please enter increment amount" },
              {
                type: "number",
                min: 1,
                message: "Amount must be greater than 0",
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter amount to add to principal"
              min={1}
              step={100}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            label="Source Account"
            name="from_account"
            rules={[
              { required: true, message: "Please select source account" },
            ]}
          >
            <Select placeholder="Select account to debit increment amount from">
              {accounts
                .filter((account) => account.status === "active")
                .map((account) => (
                  <Select.Option key={account.id} value={account.id}>
                    {account.name} - ZMW{" "}
                    {account.balance?.toLocaleString() || 0}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Payment Method"
            name="payment_method"
            rules={[
              { required: true, message: "Please select payment method" },
            ]}
            initialValue="cash"
          >
            <Select placeholder="Select payment method">
              <Select.Option value="cash">Cash</Select.Option>
              <Select.Option value="bank_transfer">Bank Transfer</Select.Option>
              <Select.Option value="mobile_money">Mobile Money</Select.Option>
            </Select>
          </Form.Item>

          {incrementCalculations && (
            <Card
              title="Increment Preview"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="New Principal"
                    value={incrementCalculations.newPrincipal}
                    prefix="ZMW"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="New Interest"
                    value={incrementCalculations.newInterestAmount}
                    prefix="ZMW"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Statistic
                    title="Principal Increase"
                    value={incrementCalculations.incrementAmount}
                    prefix="ZMW"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Interest Increase"
                    value={incrementCalculations.interestIncrease}
                    prefix="ZMW"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
              </Row>
              <Row style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Statistic
                    title="New Total Amount"
                    value={incrementCalculations.newTotalAmount}
                    prefix="ZMW"
                    valueStyle={{ color: "#722ed1", fontSize: 18 }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingIncrement}
              disabled={!incrementCalculations}
              style={{ width: "100%" }}
              icon={<DollarOutlined />}
            >
              Process Loan Increment
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Admin Loan Adjustment Modal */}
      <Modal
        title={
          <Space>
            <AlertOutlined style={{ color: "#faad14" }} />
            <span>Admin Loan Adjustment</span>
          </Space>
        }
        open={showAdjustmentModal}
        onCancel={() => {
          setShowAdjustmentModal(false);
          adjustmentForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={700}
      >
        <Alert
          message="Administrator Override"
          description="This feature allows you to manually adjust loan parameters. Use with caution. All changes are logged for audit purposes."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={adjustmentForm}
          layout="vertical"
          onFinish={handleLoanAdjustment}
        >
          <Title level={5}>Date Adjustments</Title>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Approval/Disbursement Date"
                name="approval_date"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Disbursement Date (Alternative)"
                name="disbursement_date"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Next Payment Date"
                name="next_payment_date"
                tooltip="The date when the next payment is due"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>Amount Adjustments</Title>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Principal Amount" name="amount">
                <InputNumber
                  style={{ width: "100%" }}
                  prefix="ZMW"
                  min={0}
                  step={100}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Interest Amount" name="interest_amount">
                <InputNumber
                  style={{ width: "100%" }}
                  prefix="ZMW"
                  min={0}
                  step={10}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Total Amount Due" name="total_amount_due">
                <InputNumber
                  style={{ width: "100%" }}
                  prefix="ZMW"
                  min={0}
                  step={100}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>Interest Rate Adjustments</Title>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Allow Custom Interest Rate"
                name="allow_custom_interest_rate"
                valuePropName="checked"
                tooltip="Enable custom interest rate for this loan"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Custom Interest Rate (%)"
                name="custom_interest_rate"
                tooltip="Override the default product interest rate"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  step={0.1}
                  precision={2}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Interest Applied (%)"
                name="interest_applied"
                tooltip="The actual interest rate applied to this loan (highest priority)"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  step={0.1}
                  precision={2}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Adjustment Notes (Required for audit)"
            name="adjustment_notes"
            rules={[
              {
                required: true,
                message: "Please provide a reason for this adjustment",
              },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Explain why this adjustment is being made (e.g., 'Correcting data entry error', 'Client request approved', etc.)"
            />
          </Form.Item>

          <Alert
            message="Current Values"
            description={
              <div>
                <Text>Principal: ZMW {loan?.amount?.toLocaleString()}</Text>
                <br />
                <Text>
                  Interest: ZMW {loan?.interest_amount?.toLocaleString()}
                </Text>
                <br />
                <Text>
                  Total Due: ZMW {loan?.total_amount_due?.toLocaleString()}
                </Text>
                <br />
                <Text>
                  Effective Interest Rate:{" "}
                  {getEffectiveInterestRate(loan).toFixed(2)}%
                </Text>
                <br />
                <Text>
                  Allow Custom Interest Rate:{" "}
                  {loan?.allow_custom_interest_rate ? "Yes" : "No"}
                </Text>
                <br />
                <Text>
                  Custom Interest Rate:{" "}
                  {loan?.custom_interest_rate != null
                    ? `${loan.custom_interest_rate}%`
                    : "Not set"}
                </Text>
                <br />
                <Text>
                  Interest Applied:{" "}
                  {loan?.interest_applied != null
                    ? `${loan.interest_applied}%`
                    : "Not set"}
                </Text>
                <br />
                <Text>
                  Approval Date:{" "}
                  {loan?.approval_date
                    ? moment(loan.approval_date).format("DD MMM YYYY")
                    : "Not set"}
                </Text>
                <br />
                <Text>
                  Next Payment Date:{" "}
                  {loan?.next_payment_date
                    ? moment(loan.next_payment_date).format("DD MMM YYYY")
                    : "Not set"}
                </Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setShowAdjustmentModal(false);
                  adjustmentForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submittingAdjustment}
                danger
                icon={<AlertOutlined />}
              >
                Apply Adjustment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Collateral Upload Modal */}
      <Modal
        title="Upload Collateral Documents"
        open={showUploadModal}
        onCancel={() => setShowUploadModal(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={uploadForm}
          layout="vertical"
          onFinish={handleCollateralUpload}
        >
          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please provide a description" },
            ]}
          >
            <Input.TextArea
              placeholder="Describe the collateral (e.g., household items, vehicle, property)"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="Estimated Value (ZMW)"
            name="value"
            rules={[
              { required: true, message: "Please enter estimated value" },
            ]}
          >
            <InputNumber
              placeholder="Enter collateral value"
              style={{ width: "100%" }}
              min={0}
            />
          </Form.Item>

          <Form.Item
            label="Collateral Images"
            name="collateralImages"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList || [];
            }}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const fileList = value || [];
                  if (fileList.length === 0) {
                    return Promise.reject("Please upload at least one image");
                  }
                  if (fileList.length > 10) {
                    return Promise.reject("Maximum 10 images allowed");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Upload.Dragger
              multiple
              beforeUpload={() => false}
              listType="picture"
              accept="image/*"
              maxCount={10}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag images to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for multiple images. Maximum 10 images allowed.
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={uploadingCollateral}
              block
            >
              {uploadingCollateral ? "Uploading..." : "Upload Collateral"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Flag Loan Issue"
        open={showFlagIssueModal}
        onCancel={() => setShowFlagIssueModal(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={issueForm}
          layout="vertical"
          onFinish={handleFlagIssue}
          initialValues={{ severity: "medium" }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Issue Type"
                name="issue_type"
                rules={[
                  { required: true, message: "Please select issue type" },
                ]}
              >
                <Select
                  options={ISSUE_TYPES}
                  placeholder="Select issue category"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Severity Level"
                name="severity"
                rules={[{ required: true, message: "Please select severity" }]}
              >
                <Select options={SEVERITY_LEVELS} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Issue Description"
            name="description"
            rules={[
              {
                required: true,
                message: "Please provide detailed description",
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Describe the issue in detail, including any relevant dates, amounts, or communications..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Required Follow-up Date"
                name="followup_date"
                rules={[
                  { required: true, message: "Please select follow-up date" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) => current < moment().endOf("day")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Related Collateral (if applicable)"
                name="collateral_id"
              >
                <Select
                  options={collateral.map((c) => ({
                    label: c.description,
                    value: c.id,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Supporting Evidence/Documents"
            name="attachments"
            extra="Upload relevant screenshots, documents, or images"
          >
            <Upload.Dragger
              multiple
              beforeUpload={(file) => {
                // Prevent automatic upload
                return false;
              }}
              fileList={issueForm.getFieldValue("attachments")?.fileList || []}
              onChange={({ fileList }) => {
                issueForm.setFieldsValue({ attachments: { fileList } });
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag files to this area
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingIssue}
              block
              size="large"
            >
              {submittingIssue
                ? "Submitting Issue..."
                : "Submit Formal Issue Report"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Record Promise to Pay"
        open={showPtpModal}
        onCancel={() => setShowPtpModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={ptpForm}
          layout="vertical"
          onFinish={handleCreatePtp}
          initialValues={{
            date_promised: moment(),
            reminder_date: moment().add(1, "day"),
          }}
        >
          <Form.Item
            label="Amount Promised (ZMW)"
            name="amount"
            rules={[{ required: true, message: "Please enter the amount" }]}
          >
            <InputNumber
              min={1}
              max={remainingBalance}
              style={{ width: "100%" }}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            label="Date Promised"
            name="date_promised"
            rules={[{ required: true, message: "Please select the date" }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              disabledDate={(current) => current < moment().startOf("day")}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Reminder Date"
            name="reminder_date"
            rules={[{ required: true, message: "Please select reminder date" }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                current < moment() ||
                current > ptpForm.getFieldValue("date_promised")
              }
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Reminder Message"
            name="reminder_text"
            rules={[
              { required: true, message: "Please enter reminder message" },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Example: Kindly remember your payment promise of ZMW 500 due on 28th February 2025."
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingPtp}
              block
              size="large"
            >
              {submittingPtp ? "Saving..." : "Save Promise"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* GPS Tracker Assignment Modal */}
      <Modal
        title="Assign GPS Tracker to Loan"
        open={showGpsModal}
        onCancel={() => setShowGpsModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={gpsForm}
          layout="vertical"
          onFinish={handleAssignGpsTracker}
        >
          <Form.Item
            label="Select GPS Tracker"
            name="tracker_id"
            rules={[{ required: true, message: "Please select a GPS tracker" }]}
          >
            <Select
              placeholder="Choose from available GPS trackers"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option.children.toString();
                return text.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {availableTrackers.map((tracker) => (
                <Select.Option key={tracker.id} value={tracker.id}>
                  GPS: {tracker.gps_code} | Serial: {tracker.serial_number} |
                  SIM: {tracker.sim_number}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {availableTrackers.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Text type="secondary">
                No GPS trackers are currently available for assignment. All
                trackers are either in use or require maintenance.
              </Text>
            </div>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingGps}
              block
              disabled={availableTrackers.length === 0}
            >
              {submittingGps ? "Assigning..." : "Assign GPS Tracker"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Outstanding Debt Creation Modal */}
      <Modal
        title="Record Outstanding Debt"
        open={showDebtModal}
        onCancel={() => setShowDebtModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={debtForm} layout="vertical" onFinish={handleCreateDebt}>
          <Form.Item
            label="Debt Type"
            name="debt_type"
            rules={[{ required: true, message: "Please select debt type" }]}
          >
            <Select placeholder="Select type of debt" options={debtTypes} />
          </Form.Item>

          <Form.Item
            label="Amount Owing (ZMW)"
            name="amount_owing"
            rules={[
              { required: true, message: "Please enter the amount owing" },
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
              placeholder="Enter amount owing"
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please provide a description" },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Describe what this debt is for (e.g., Legal fees for debt collection, Court filing fees, etc.)"
            />
          </Form.Item>

          <Form.Item label="Due Date (Optional)" name="due_date">
            <DatePicker
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              placeholder="When should this debt be paid?"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingDebt}
              block
              size="large"
            >
              {submittingDebt ? "Recording..." : "Record Outstanding Debt"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Transaction Reversal Confirmation Modal */}
      <Modal
        title={
          <div
            style={{ display: "flex", alignItems: "center", color: "#ff4d4f" }}
          >
            <ExclamationCircleOutlined style={{ marginRight: 8 }} />
            Confirm Transaction Reversal
          </div>
        }
        open={showReversalModal}
        onCancel={() => {
          setShowReversalModal(false);
          setSelectedTransactionForReversal(null);
        }}
        footer={null}
        destroyOnClose
        width={600}
      >
        {selectedTransactionForReversal && (
          <div>
            <div
              style={{
                padding: 16,
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              <Text strong style={{ color: "#ff4d4f" }}>
                ⚠️ WARNING: This action cannot be undone
              </Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  This will create an offsetting transaction and update all
                  affected balances.
                </Text>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16 }}>
                Transaction to Reverse:
              </Text>
            </div>

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Amount">
                <Text strong style={{ fontSize: 16 }}>
                  ZMW {selectedTransactionForReversal.amount?.toLocaleString()}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Transaction Type">
                <Tag color="blue">
                  {selectedTransactionForReversal.transaction_type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {new Date(
                  selectedTransactionForReversal.transaction_date,
                ).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Reference Number">
                <Text code>
                  {selectedTransactionForReversal.reference_number}
                </Text>
              </Descriptions.Item>
              {selectedTransactionForReversal.notes && (
                <Descriptions.Item label="Notes">
                  {selectedTransactionForReversal.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 20 }}>
              <Text strong>What will happen:</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {selectedTransactionForReversal.transaction_type ===
                  "Loan Repayment" && (
                  <>
                    <li>
                      Loan's paid amount will be reduced by ZMW{" "}
                      {selectedTransactionForReversal.amount?.toLocaleString()}
                    </li>
                    <li>
                      Loan status may change back to Active if fully reversed
                    </li>
                  </>
                )}
                {selectedTransactionForReversal.transaction_type ===
                  "Debt Repayment" && (
                  <>
                    <li>
                      Debt's paid amount will be reduced by ZMW{" "}
                      {selectedTransactionForReversal.amount?.toLocaleString()}
                    </li>
                    <li>Debt status will change back to Active</li>
                  </>
                )}
                {selectedTransactionForReversal.transaction_type ===
                  "Loan Disbursement" && (
                  <>
                    <li>Money will be returned to the disbursement account</li>
                    <li>Loan status will change back to Pending Approval</li>
                  </>
                )}
                <li>
                  A reversal transaction with negative amount (-
                  {selectedTransactionForReversal.amount?.toLocaleString()})
                  will be created
                </li>
                <li>Original transaction will be marked as REVERSED</li>
              </ul>
            </div>

            <div
              style={{
                marginTop: 24,
                textAlign: "right",
                borderTop: "1px solid #f0f0f0",
                paddingTop: 16,
              }}
            >
              <Button
                onClick={() => {
                  setShowReversalModal(false);
                  setSelectedTransactionForReversal(null);
                }}
                style={{ marginRight: 8 }}
                disabled={submittingReversal}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                loading={submittingReversal}
                onClick={() =>
                  handleTransactionReversal(selectedTransactionForReversal)
                }
                icon={<UndoOutlined />}
              >
                {submittingReversal
                  ? "Reversing Transaction..."
                  : "Confirm Reversal"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reloan Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: "#fa8c16",
            }}
          >
            <SyncOutlined style={{ marginRight: 8 }} />
            Manual Reloan - {loan?.borrower?.first_name}{" "}
            {loan?.borrower?.last_name}
          </div>
        }
        open={showReloanModal}
        onCancel={() => {
          setShowReloanModal(false);
          reloanForm.resetFields();
          setReloanCalculations(null);
        }}
        onOk={() => reloanForm.submit()}
        okText="Process Reloan"
        width={700}
        okButtonProps={{
          style: {
            backgroundColor: "#fa8c16",
            borderColor: "#fa8c16",
          },
        }}
      >
        {reloanCalculations && (
          <>
            <Alert
              type="info"
              message="Reloan Calculation Summary"
              description={
                <div>
                  <div>
                    Original Loan Amount: ZMW{" "}
                    {reloanCalculations.originalAmount.toLocaleString()}
                  </div>
                  <div>
                    Total Amount Due: ZMW{" "}
                    {reloanCalculations.totalAmountDue.toLocaleString()}
                  </div>
                  <div>
                    Interest Amount: ZMW{" "}
                    {reloanCalculations.interestAmount.toLocaleString()}
                  </div>
                  <div style={{ marginTop: 8, fontWeight: "bold" }}>
                    Total Paid: ZMW{" "}
                    {reloanCalculations.totalPaid.toLocaleString()}
                  </div>
                  <div>
                    Principal Paid: ZMW{" "}
                    {reloanCalculations.principalPaid.toLocaleString()}
                  </div>
                  <div>
                    Interest Paid: ZMW{" "}
                    {reloanCalculations.interestPaid.toLocaleString()}
                  </div>
                </div>
              }
              style={{ marginBottom: 16 }}
            />

            <Form
              form={reloanForm}
              onFinish={handleReloanSubmit}
              layout="vertical"
            >
              <Form.Item
                label="Remaining Principal to Reloan (ZMW)"
                name="reloan_amount"
                help="This is the remaining principal balance from the original loan"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  readOnly
                  formatter={(value) =>
                    `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                />
              </Form.Item>

              <Form.Item
                label="Outstanding Interest Debt (ZMW)"
                name="outstanding_debt"
                help="This outstanding interest will be recorded as a separate debt"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  readOnly
                  formatter={(value) =>
                    `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                />
              </Form.Item>

              <Form.Item
                label="Reloan Date"
                name="reloan_date"
                help="Select the date from which the reloan will apply (leave blank for today)"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select reloan date (optional)"
                  format="YYYY-MM-DD"
                />
              </Form.Item>

              <Alert
                type="warning"
                message="Reloan Process"
                description="The system will: 1) Create a new loan with the remaining principal amount, 2) Record outstanding interest as debt, 3) Apply fresh interest terms based on the loan product, 4) Set new payment schedule starting from selected date (or today if no date selected), 5) Mark the original loan as reloaned, 6) Navigate to the new loan"
                style={{ marginTop: 16 }}
              />
            </Form>
          </>
        )}
      </Modal>

      {/* Loan Deletion Confirmation Modal */}
      <Modal
        title={
          <div
            style={{ display: "flex", alignItems: "center", color: "#ff4d4f" }}
          >
            <DeleteOutlined style={{ marginRight: 8 }} />
            Confirm Loan Deletion
          </div>
        }
        open={showDeleteModal}
        onCancel={() => !deletingLoan && setShowDeleteModal(false)}
        footer={null}
        closable={!deletingLoan}
        maskClosable={!deletingLoan}
        width={700}
      >
        <Form form={deleteForm} layout="vertical" onFinish={confirmDeleteLoan}>
          <Alert
            message="⚠️ DANGER: This action cannot be undone!"
            description="Deleting this loan will permanently remove all related data. You can optionally refund the loan amount to a selected account."
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />

          {deletionPreview && (
            <>
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Loan Information</Title>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Borrower: </Text>
                    <Text>
                      {deletionPreview.loan.borrower.first_name}{" "}
                      {deletionPreview.loan.borrower.last_name}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>Amount: </Text>
                    <Text>
                      ZMW {deletionPreview.loan.amount?.toLocaleString()}
                    </Text>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Text strong>Status: </Text>
                    <Tag
                      color={statusMap[deletionPreview.loan.loan_status]?.color}
                    >
                      {statusMap[deletionPreview.loan.loan_status]?.text}
                    </Tag>
                  </Col>
                  <Col span={12}>
                    <Text strong>Approved: </Text>
                    <Text>
                      {new Date(
                        deletionPreview.loan.approval_date,
                      ).toLocaleDateString()}
                    </Text>
                  </Col>
                </Row>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Data to be Deleted</Title>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Transactions"
                      value={deletionPreview.relatedData.transactionCount}
                      prefix={<FireOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Debt Records"
                      value={deletionPreview.relatedData.debtCount}
                      prefix={<DollarOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Affected Accounts"
                      value={deletionPreview.relatedData.affectedAccounts || 0}
                      prefix={<BankOutlined />}
                    />
                  </Col>
                </Row>

                {deletionPreview.relatedData.commodityDetailsCount > 0 && (
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={8}>
                      <Statistic
                        title="Commodity Details"
                        value={
                          deletionPreview.relatedData.commodityDetailsCount
                        }
                        prefix={<FireOutlined />}
                      />
                    </Col>
                  </Row>
                )}
              </div>

              {deletionPreview.relatedData.hasCollateral && (
                <Alert
                  message="Collateral Impact"
                  description="Associated collateral will be marked as 'Released' and can be used for other loans."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Form.Item
                label="Refund Account (Optional)"
                name="refund_account_id"
                rules={[{ required: false }]}
                style={{ marginBottom: 16 }}
                help="Select an account to refund the loan amount. If not selected, no refund will be processed."
              >
                <Select
                  placeholder="Select account to receive the refunded loan amount (optional)"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {accounts
                    ?.filter(
                      (account) =>
                        account.status === "active" || !account.status,
                    )
                    .map((account) => (
                      <Select.Option key={account.id} value={account.id}>
                        {account.name} - {account.account_type} (Balance: ZMW{" "}
                        {account.balance?.toLocaleString()})
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Alert
                message="Refund Process"
                description={
                  <>
                    <div>
                      When you delete this loan, the loan amount (ZMW{" "}
                      {deletionPreview.loan.amount?.toLocaleString()}) will be
                      added to the selected account.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>
                        Amount to be refunded: ZMW{" "}
                        {deletionPreview.loan.amount?.toLocaleString()}
                      </strong>
                    </div>
                  </>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              onClick={() => setShowDeleteModal(false)}
              disabled={deletingLoan}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              htmlType="submit"
              loading={deletingLoan}
              icon={<DeleteOutlined />}
            >
              {deletingLoan ? "Deleting..." : "Delete Loan"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit Installment Modal */}
      <Modal
        title="Edit Installment"
        open={!!editingInstallment}
        onCancel={() => {
          setEditingInstallment(null);
          editScheduleForm.resetFields();
        }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={editScheduleForm}
          layout="vertical"
          onFinish={handleSaveInstallment}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Month" name="month">
                <Input placeholder="e.g., AUGUST 2021" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Due Date" name="due_date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Amount Due (Principal)" name="amount_due">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0.00"
                  prefix="ZMW"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Interest Rate (%)" name="interest_rate">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Expected Amount (Total)" name="expected_amount">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0.00"
                  prefix="ZMW"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Profit (Interest Amount)" name="profit">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0.00"
                  prefix="ZMW"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Status" name="status">
            <Select>
              <Select.Option value="pending">Not Paid</Select.Option>
              <Select.Option value="paid">Paid</Select.Option>
              <Select.Option value="cleared">Cleared</Select.Option>
              <Select.Option value="overdue">Overdue</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setEditingInstallment(null);
                  editScheduleForm.resetFields();
                }}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={savingEdit}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Amortization Schedule Modal */}
      <Modal
        title="Add Amortization Schedule"
        open={showUploadScheduleModal}
        onCancel={() => {
          setShowUploadScheduleModal(false);
          setScheduleFile(null);
          scheduleForm.resetFields();
          setUploadMode("single");
        }}
        footer={null}
        width={700}
      >
        <Tabs
          activeKey={uploadMode}
          onChange={setUploadMode}
          items={[
            {
              key: "single",
              label: "Single Entry",
              children: (
                <Form
                  form={scheduleForm}
                  layout="vertical"
                  onFinish={handleUploadSingleSchedule}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Month"
                        name="month"
                        rules={[
                          { required: true, message: "Please enter month" },
                        ]}
                      >
                        <Input placeholder="e.g., AUGUST 2021" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Due Date"
                        name="due_date"
                        rules={[
                          { required: true, message: "Please select due date" },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Amount Due (Principal)"
                        name="amount_due"
                        rules={[
                          {
                            required: true,
                            message: "Please enter amount due",
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          placeholder="0.00"
                          prefix="ZMW"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Interest Rate (%)"
                        name="interest_rate"
                        rules={[
                          {
                            required: true,
                            message: "Please enter interest rate",
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          max={100}
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Expected Amount (Total)"
                        name="expected_amount"
                        rules={[
                          {
                            required: true,
                            message: "Please enter expected amount",
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          placeholder="0.00"
                          prefix="ZMW"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Profit (Interest Amount)"
                        name="profit"
                        rules={[
                          { required: true, message: "Please enter profit" },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          placeholder="0.00"
                          prefix="ZMW"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Status"
                    name="status"
                    initialValue="pending"
                    rules={[
                      { required: true, message: "Please select status" },
                    ]}
                  >
                    <Select>
                      <Select.Option value="pending">Not Paid</Select.Option>
                      <Select.Option value="paid">Paid</Select.Option>
                      <Select.Option value="cleared">Cleared</Select.Option>
                      <Select.Option value="overdue">Overdue</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={uploadingSchedule}
                      block
                    >
                      Add Schedule Entry
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: "bulk",
              label: "Bulk Upload (CSV)",
              children: (
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Alert
                    message="CSV Format Required"
                    description={
                      <div>
                        <p>Your CSV file must contain the following columns:</p>
                        <ul>
                          <li>
                            <strong>month</strong> - Month name (e.g., "AUGUST
                            2021")
                          </li>
                          <li>
                            <strong>due_date</strong> - Due date (YYYY-MM-DD
                            format)
                          </li>
                          <li>
                            <strong>amount_due</strong> - Principal amount
                          </li>
                          <li>
                            <strong>interest_rate</strong> - Interest rate as
                            percentage
                          </li>
                          <li>
                            <strong>expected_amount</strong> - Total amount
                            (principal + interest)
                          </li>
                          <li>
                            <strong>profit</strong> - Interest amount
                          </li>
                          <li>
                            <strong>status</strong> - Payment status (paid,
                            pending, etc.)
                          </li>
                          <li>
                            <strong>sheet</strong> - (Optional) Source sheet
                            name
                          </li>
                        </ul>
                      </div>
                    }
                    type="info"
                    showIcon
                  />

                  <Upload
                    beforeUpload={(file) => {
                      if (!file.name.endsWith(".csv")) {
                        message.error("Please upload a CSV file");
                        return false;
                      }
                      setScheduleFile(file);
                      return false;
                    }}
                    onRemove={() => setScheduleFile(null)}
                    maxCount={1}
                    accept=".csv"
                  >
                    <Button icon={<UploadOutlined />}>Select CSV File</Button>
                  </Upload>

                  {scheduleFile && (
                    <Text type="success">Selected: {scheduleFile.name}</Text>
                  )}

                  <Button
                    type="primary"
                    onClick={handleUploadSchedule}
                    loading={uploadingSchedule}
                    disabled={!scheduleFile}
                    block
                  >
                    Upload CSV
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default LoanDetailsPage;
